// Build-time feature flags. Mirror the backend env.bool() convention: each flag
// defaults OFF and is flipped per environment via a REACT_APP_* build var. When
// OFF the gated UI is byte-identical to today (no new surfaces rendered).
const truthy = (v) => v === "true" || v === "1" || v === true;

// NEW_LOGIN_ENABLED — the "Living Nebula" Login hero (ISP-9999; design source
// C:\iYuga\designs\login-fable-magic\creative\flagship-living-nebula-v2.html). A
// full-bleed blue nebula in which on-brand Wheels coalesce into the iYuga logo
// swoosh (one persistent cascade flash, product-matched separator strokes), with
// the EXISTING login form rendered on top as a glass card. OFF (default) keeps the
// login page BYTE-IDENTICAL to today: AuthWrapper renders its current split layout
// (form left, static image OR the GR-7 LoginWheelAnim right) and LoginNebula is
// never mounted — no new DOM, no rAF loop, no network. ON (login only — it rides
// the `heroAnimation` opt-in that ONLY Login passes; Signup/ForgotPassword are
// untouched) swaps in the full-bleed nebula + glass-card layout. The animation is
// aria-hidden, pointer-events:none, respects prefers-reduced-motion (still formed
// mark, no flashing), pauses when the tab is hidden, and cleans up on unmount, so
// it never blocks or delays the form. This flag is UI-only — it does NOT touch the
// auth logic (the Formik form + all its handlers are passed through unchanged as
// AuthWrapper `children`). A critical auth page: deploy DARK, flip ON only after
// live QA on web-stg confirms login still works + the animation matches the mockup.
export const NEW_LOGIN_ENABLED = truthy(process.env.REACT_APP_NEW_LOGIN);

// LOGIN_PORTRAITS_ENABLED — additive on top of NEW_LOGIN_ENABLED: restores the approved
// mockup's real portrait PHOTOS into the Living Nebula wheel center discs (the port kept
// loginWheelSVG's `portrait` param but always fed it ""). Photos are inlined base64 webp
// data URIs (NETWORK-FREE — zero runtime fetch; ~46KB total, <0.3% of the page bundle). OFF
// (default) keeps the nebula BYTE-IDENTICAL to today: white/empty wheel centers, no <image>
// in the DOM. ON draws the portraits in the 4 hero centers + a tasteful subset of minis, and
// swaps the reduced-motion frame to a static full-content composition (heroes full-size WITH
// portraits, gradient applied, wordmark formed) instead of collapsing to invisible beads.
export const LOGIN_PORTRAITS_ENABLED = truthy(process.env.REACT_APP_LOGIN_PORTRAITS);

// API_KEYS_PORTAL_ENABLED - Settings > API Keys developer portal (SDK W1b:
// mint/list/revoke keys + per-key usage). Default OFF: when OFF the Settings
// page renders byte-identical to today (no API Keys tab registered, no portal
// network calls). When ON, the tab appears; an entitlement gate inside the
// portal still shows an upsell to non-Business users (see ApiKeys/index.jsx).
export const API_KEYS_PORTAL_ENABLED = truthy(
  process.env.REACT_APP_API_KEYS_PORTAL
);

// GALLERY_TEAMS_CONTEXT_ENABLED — the gallery Personal/Team context switcher
// (SPEC-2026-08-18). Default OFF: when OFF the owner gallery renders
// byte-identical to today (no switcher, no team scoping, no team chip; the
// personal folders + Created/Cloned/Unlocked/Favorited tabs are untouched).
// When ON *and* the Teams feature (REACT_APP_TEAMS_ENABLED) is ON *and* the
// viewing owner belongs to ≥1 team, a "Personal / <team>" switcher appears in
// the Wheel-Grid header bar (ISP-9999 #404 — the slot the "Create folder" icon
// used to occupy, now redundant with the Collections section's own add-card)
// and scopes the wheel list to the chosen context.
// Rides on top of the Teams flag — it can do nothing without it.
export const GALLERY_TEAMS_CONTEXT_ENABLED = truthy(
  process.env.REACT_APP_GALLERY_TEAMS_CONTEXT_ENABLED
);

/**
 * Manual Brand Palette builder (pick-your-exact-hex mode) alongside the existing
 * auto generator. DRAFT / dev-review only. Default OFF — when OFF, the palette
 * modal renders exactly the existing Prompt/Image auto tabs, unchanged.
 */
export const PALETTE_MANUAL_BUILDER = truthy(
  process.env.REACT_APP_PALETTE_MANUAL_BUILDER
);

// PALETTE_MODAL_V2_ENABLED — decision D15: consolidate the fragmented palette
// surfaces into ONE modal with three tabs — Prompt | Image | Hex. OFF (default)
// keeps the palette modal BYTE-IDENTICAL to today: ColorPaletteModal renders its
// existing Prompt/Image tabs (+ the separate PALETTE_MANUAL_BUILDER "Manual"
// draft tab when that flag is on), the standalone BrandPaletteWizard is reached
// only from the Color Library's "New palette" entry (its own
// REACT_APP_BRAND_PALETTE_WIZARD flag), the swallowed generator error strings
// stay hidden, palette delete uses no confirm, and every V2 copy/UX polish is
// inert. ON folds the deck-faithful hex builder (BrandPaletteWizard/logic) in as
// the modal's third "Hex" tab (retiring the ManualPalette draft + the
// commented-out dead SegmentColor tab), so a hex palette is committed through the
// SAME single modal "Select" button as Prompt/Image (one confirm path, D-793),
// and turns on the bundled in-surface fixes: render the Prompt/Image generator
// errors (786/790), route palette delete through the shared DeleteModal confirm
// (791), humanize the interpolated subtitle copy (784), and make the empty-state
// buttons fluid (792). Touches no money/auth/network path — pure presentational
// consolidation over the existing savePalette contract. Pairs with no BE flag
// (the additive `custom_built` palette tag is inert extra JSON).
export const PALETTE_MODAL_V2_ENABLED = truthy(
  process.env.REACT_APP_PALETTE_MODAL_V2
);

// LINKVAULT_PALETTE_LIBRARY_ENABLED — DL-064 "Palette Library" on the LinkVault
// "Vault Design" tab (Gallery Settings -> LinkVault). Answers Gordon's deck note:
// "pick from your saved palettes (think Brand colors)" + "allow users to save
// palettes manually". OFF (default) keeps the Vault Design tab byte-identical to
// today: VaultDesign renders only its Background color / Wallpaper / Accent
// fields, no Palette Library section, and the LinkVault update payload is
// unchanged (it already sends vault_theme). ON adds a Palette Library section at
// the TOP of the Vault Design tab — a "My Palettes" list (up to 5 circle
// swatches per row, selected row ringed + checked), a "Save current palette"
// action, and a Color Library popup (library-icon) to browse/select saved brand
// palettes. Saved palettes persist INSIDE the already-wired UserProfile
// vault_theme JSON (vault_theme.saved_palettes / active_palette_id) via the same
// user-update-profile mutation LinkVault already calls — no new endpoint, no
// migration. Selecting a palette maps its colors onto the theme (bg_color /
// accent). Server-side persistence additionally requires the backend
// LINKVAULT_THEMING_ENABLED setting (the vault_theme write seam); this FE flag
// governs the UI only. Touches no money/auth path.
export const LINKVAULT_PALETTE_LIBRARY_ENABLED = truthy(
  process.env.REACT_APP_LINKVAULT_PALETTE_LIBRARY
);

// SHARE_MODAL_V2_ENABLED — redesigned ShareModal (specs deck sharing/01). OFF
// (default) keeps the modal byte-identical to today: the current icon-row +
// wheel-preview layout renders exactly as before (WhatsApp still commented out,
// no section labels, copy is the trailing grey icon only). ON swaps in the deck
// layout — a labeled "COPY LINK" row showing the share URL with a purple Copy
// button (Copy -> "Copied" confirmation), and a "SHARE TO" section whose label
// sits above the SAME social share buttons/handlers (Facebook, Pinterest,
// LinkedIn, X, Messenger, WhatsApp, + trailing copy icon). Reuses the existing
// react-share ShareButton wrapper and copyToClipboard handler; adds no network
// call and touches no money/auth path. The one flag-gated path covers every
// ShareModal consumer (gallery cards, ViewWheel, builder).
export const SHARE_MODAL_V2_ENABLED = truthy(
  process.env.REACT_APP_SHARE_MODAL_V2
);

// ICON_NO_ICON_TILE_ENABLED — adds a selectable dashed "No icon" tile as the
// FIRST item in the segment icon-picker grid (SegmentSettings/children/
// SegmentIcon.jsx). Gordon's verbatim requirement (audit O4 / DL-045): the
// segment icon picker must have a selectable "No Icon / None" option. Selecting
// the tile clears the segment's icon via the SAME onIconSelect("") path the
// existing accordion clear affordance (HighlightOffIcon) already uses — no new
// data field, no backend contract. OFF (default) keeps the picker
// byte-identical to today: the None tile is not inserted, the grid index math
// and row count are unchanged, and the picker renders exactly its current
// icon-only grid. Rides on top of REACT_APP_SEGMENT_ADAPTIVE_ICONS_ENABLED (the
// picker surface itself); this flag only governs the None tile. Touches no
// money/auth path.
export const ICON_NO_ICON_TILE_ENABLED = truthy(
  process.env.REACT_APP_ICON_NO_ICON_TILE
);

// ICON_SUBJECT_CATEGORIES_ENABLED — replaces the segment icon picker's category
// tabs with SUBJECT / THEME categories (People, Food & Drink, Travel & Places,
// Technology, ...) instead of the icon pack's STYLE families.
//
// The gap this closes (DESIGN-DEBT-REGISTER DL-045, deck concept
// adaptive-icons-elevated/04-segment-icon-picker): `faCategories.js` exports
// exactly three groups — `Regular`, `Brands`, `Solid` — and `iconSource.js`
// adds a `More` catch-all. Those describe how FontAwesome draws an icon, not
// what it depicts, so the tabs are useless to somebody hunting an icon for a
// "Travel" or "Fitness" Segment; today the only real way through ~1,500 icons
// is to already know the icon's name.
//
// Classification is derived from each icon's own name tokens by the pure
// taxonomy in SegmentBuilder/Shared/SegmentStyling/logic/iconSubjects.js
// (measured: 74% of the real catalog lands in a meaningful subject, the rest in
// "Other" — no icon is ever unreachable). No new data, no backend contract, no
// new dependency.
//
// OFF (default) keeps BOTH pickers byte-identical to today: the category map is
// passed through untouched, the subject index is never built, and the tabs
// render the existing style families. Touches no money/auth path.
export const ICON_SUBJECT_CATEGORIES_ENABLED = truthy(
  process.env.REACT_APP_ICON_SUBJECTS
);

// EMBED_URL_REHOST_ENABLED — makes the Embed-URL feature (Wheel Settings ->
// Portrait/Wallpaper -> paste an external image URL) fetch + moderate + re-host
// the image server-side instead of hotlinking the raw external URL. OFF
// (default) keeps the Embed-URL flow byte-identical to today: Confirm still runs
// the client-side fetch/upload (with the save-external-image CORS fallback) and
// the "may not display correctly on social media" warning is shown. ON routes
// Confirm through the backend /api/v2/wheel/embed-url-rehost/ endpoint (SSRF
// guard + OpenAI moderation + S3 re-host), so the returned src is a moderated
// S3 URL; a moderation flag shows the same inline reject UX as an upload (the
// image is not inserted), and the social-media warning is removed because the
// re-hosted image no longer has that limitation. Pairs with the backend
// EMBED_URL_REHOST_ENABLED Django setting. Touches no money/auth path.
export const EMBED_URL_REHOST_ENABLED = truthy(
  process.env.REACT_APP_EMBED_URL_REHOST
);

// GALLERY_THEMING_ENABLED — creator theming for the public Gallery/profile page
// (#1709 · Tier-L theme engine; design-handoff/tabs/gallery-theming.html). OFF
// (default) keeps the Gallery byte-identical to today: no "Gallery Theme" tab or
// route is rendered, the gallery background stays the current hard-coded #E8E8E8,
// and no theme is ever read or written. ON adds a self-contained theming studio
// (Preset looks Iris/Dusk/Bloom · Wallpaper Color|Gradient|Pattern|Image ·
// Accent · Background · Palette Library · Customize/Advanced) with a live
// preview, persists the chosen theme on UserProfile.gallery_theme via the SAME
// update-profile PATCH the LinkVault theming uses, and applies the saved theme
// to the real Gallery page. Mirrors the LinkVault "Vault Design" engine
// (vaultTheme.js) — same value-agnostic resolvers + CSS-injection guards — and
// re-integrates the existing vault_theme via a "Match Link Vault" toggle. All
// theme values are user-supplied and pass the strict color/gradient/url guards
// before touching the DOM, so the flag-OFF path renders zero new surface.
export const GALLERY_THEMING_ENABLED = truthy(
  process.env.REACT_APP_GALLERY_THEMING
);

// BUILD_MENU_V2_ENABLED — additive polish for the Edit-Wheel "Build Menu" block
// palette (SegmentBuilder/Toolbar). Gordon's 3 asks + Accordion re-home. OFF
// (default) keeps the palette byte-identical to today: the section list, order,
// icon rendering (plain <img>) and the scroll container are unchanged. ON applies
// three flag-gated changes: (1) SCROLL — caps the palette scroll viewport
// (Toolbar mainStack) to its parent height + adds bottom clearance so the last
// Interactive tile is reachable past the fixed first-run coach card (was clipped
// by a 92vh child inside a 90vh overflow:hidden parent); (2) ICON COLORS — recolors
// each tile icon to its section colour via CSS mask (orange=Layout, green=Content,
// blue=Interactive) using existing theme.js tokens; (3) GROUPING — re-orders the
// Content section (text -> image -> media) and moves Accordion into Layout (with
// Container + Divider). Pure client-side chrome/CSS + a constants reorder; touches
// no data/money/auth path.
export const BUILD_MENU_V2_ENABLED = truthy(
  process.env.REACT_APP_BUILD_MENU_V2
);

// BUILDER_PANE_SCROLL_FIX_ENABLED — fixes the bottom-clip in the two builder side
// panes whose scroll region is sized to the viewport (vh) yet nested inside the
// shared RightSection/SegmentSetting `Container` (Toolbar/styles: height:90vh;
// overflow:hidden). That Container starts ~137px (13.7vh) down the page, so its
// own bottom already sits ~37px below a 1000px viewport; a 92vh/74vh inner scroll
// region therefore overshoots the viewport and the LAST element (e.g. the Text
// block's Font control, or the Segment Settings "Segment Icon" section once
// "Segment Color" is expanded) lands in the off-screen band and cannot be scrolled
// into view. ON caps each pane's OWN scroll region to the viewport
// (max-height: calc(100vh - 200px)) and adds padding-bottom clearance so the last
// element is always reachable after scroll (guaranteed while the region starts
// <=296px from top; measured 182-223px). Two surfaces are gated:
//   (1) SegmentBuilder/Shared/SidePaneWrapper.jsx — the Segment Settings pane scroll.
//   (2) SegmentBuilder/Toolbar/Settings/Settings.jsx — the per-block editor pane
//       scroll (decoupled from BUILD_MENU_V2, which only masked this on staging).
// OFF (default) keeps both scroll containers byte-identical to today (the exact
// prior inline style objects: 92vh/74vh heights). Pure presentational CSS; touches
// no data/money/auth path. Wheel Settings (Main/Sidepane) uses a different
// container and is untouched.
export const BUILDER_PANE_SCROLL_FIX_ENABLED = truthy(
  process.env.REACT_APP_BUILDER_PANE_SCROLL_FIX
);

// BLOCK_CLICK_ADD_ENABLED — adds a click / keyboard (Enter/Space) path to the
// Build Menu block-picker tiles (Shared/Card), so a block can be added to the
// current segment canvas without a mouse drag (accessibility: today the tiles are
// drag-ONLY via react-dnd/craft connectors.create, unreachable by keyboard).
// ON: clicking or pressing Enter/Space on a tile appends that block to the segment
// root (craft "ROOT" canvas) — the same node tree a drag-to-empty-space produces.
// Drag is untouched and still works. OFF (default) => the tiles are byte-identical
// to today (drag-only), so the merge is inert on prod until flipped.
// Merge-train staging env line: REACT_APP_BLOCK_CLICK_ADD=true.
export const BLOCK_CLICK_ADD_ENABLED = truthy(
  process.env.REACT_APP_BLOCK_CLICK_ADD
);

// SEGMENT_ADAPTIVE_ICONS_ENABLED — the wheel-owner activation surface for the
// adaptive segment-icon render engine (createPie.js updateIcons()). The engine
// ships unconditionally and adapts each per-segment icon to the segment's size
// (full -> dot -> none) and contrast color, but it only draws when the wheel's
// persisted `segment_icon` setting is "icons"/"both"; the deployed default is
// "numbers" so NOTHING renders (the feature was INERT — flag ON, no data/UI
// path to flip it). This flag governs (a) the builder control that writes the
// wheel-level `segment_icon` setting and (b) the render wiring that feeds that
// setting into config.segmentIconConfig.showOnSegment. OFF (default) keeps
// every surface byte-identical: no control is rendered and no segmentIconConfig
// is passed, so the engine keeps its "numbers" default and no icon draws.
// (ICON_NO_ICON_TILE_ENABLED rides on top of this — the picker's None tile.)
// Touches no money/auth path.
export const SEGMENT_ADAPTIVE_ICONS_ENABLED = truthy(
  process.env.REACT_APP_SEGMENT_ADAPTIVE_ICONS_ENABLED
);

// MEDIA_RECORDER_ENABLED — in-browser camera/mic capture for the Image, Video
// and Audio content blocks (and, later, the narrator-Portrait + share-video
// narration). OFF (default) keeps the blocks upload-only and byte-identical to
// today: no "Record"/"Take Photo" tab is rendered and the capture chunk is
// never fetched. ON adds a capture option alongside "Upload" per block type —
// Video/Audio get "Record" (getUserMedia + MediaRecorder, MediaCapture/
// MediaRecorder.jsx), Image gets "Take Photo" (getUserMedia + canvas still,
// MediaCapture/WebcamPhotoCapture.jsx) — then routes the captured Blob/File
// through the SAME upload path an uploaded file already takes (Video/Audio:
// utils/fileUpload -> handleFileUpload via handleChange; Image: the block's
// own acceptUpload -> onUpload, ImageEditorSettings.jsx's real S3 presigned
// pipeline). Each tab is additionally feature-detected per browser
// (canRecordMedia / canCapturePhoto in components/MediaCapture) so an
// unsupported browser (no getUserMedia, or no MediaRecorder codec support)
// never sees a dead-end capture tab — it gets Upload only, silently.
export const MEDIA_RECORDER_ENABLED = truthy(
  process.env.REACT_APP_MEDIA_RECORDER
);

// RENDERER_V2_ENABLED — the v2 wheel render engine (WL-054 / SDD-22 /
// ROAD-32). OFF (default) keeps every wheel byte-identical to today: the
// engine module (src/renderer-v2/) is never mounted and NO existing surface
// switches to it. ON exposes ONLY the hidden /renderer-v2-compare page — a
// v1-vs-v2 side-by-side parity harness (same wheel data through both engines
// with a measured geometry/color readout). Migration of real surfaces
// (embed → view → builder, per the SDD-22 strangler-fig plan) is each its own
// later, separately-flagged step. Touches no money/auth path.
export const RENDERER_V2_ENABLED = truthy(process.env.REACT_APP_RENDERER_V2);

// RENDERER_V2_VIEW_ENABLED — per-surface strangler flip of the PUBLIC ViewWheel
// (/wheel/<id>) onto the v2 render engine (SDD-22 W2 · ADR-0001). OFF (default)
// keeps the public wheel byte-identical to today: `ViewWheelRender` renders the
// legacy `<WheelComponent>` (D3 createPie) verbatim, so no existing pixel, event
// or DOM changes. ON renders the SAME wheel data through the v2 engine via the
// LOSSLESS v1→v2 adapter (fromV1) — a READ-ONLY shadow-flip: no save-path, no
// schema-authority, no wallpaper/env or Segment-popup changes (those are the
// page's, untouched). This is its OWN flag, independent of REACT_APP_RENDERER_V2
// (which gates only the hidden /renderer-v2-compare + /renderer-v2-view harness
// routes) — flipping the view never flips the gallery/embed/builder, and the
// rollback is simply leaving this flag unset. Rim pills still follow
// WHEEL_PILL_LABELS_ENABLED in BOTH engines, so both converge on pills together.
export const RENDERER_V2_VIEW_ENABLED = truthy(
  process.env.REACT_APP_RENDERER_V2_VIEW
);

// RESIZE_WEDGE_DRAG_ENABLED — W-A0 Segment wedge-edge drag-resize (deck:
// design-handoff/concepts/segment-resize). OFF (default) keeps the builder
// byte-identical to today: NO drag overlay mounts on the wheel canvas, and the
// right-rail "Segments" resize tab stays span-by-slider only. ON adds boundary
// drag handles on the builder wheel — drag a wedge's edge to trade width
// between Segments (Neighbour by default; hold Shift to spread across all
// unlocked Segments), writing the SAME per-Segment `value` the sliders edit
// through the existing withHistory(setWheelSegments) path (no new data model,
// no schema change). Ships on the CURRENT renderer — does NOT touch
// RENDERER_V2. The sliders remain the keyboard-accessible equivalent. Touches
// no money/auth path.
export const RESIZE_WEDGE_DRAG_ENABLED = truthy(
  process.env.REACT_APP_RESIZE_WEDGE_DRAG
);

// RENDERER_V2_SAVE_ENABLED — the de-stormed, versioned save pipeline (W3 /
// LW-01 + LW-02). OFF (default) keeps the Wheel Builder save path byte-identical
// to today: no edit_version token is sent, no 409 is reconciled, and saves are
// not coalesced. ON makes the builder (1) send the edit_version it loaded on
// every PUT /api/v2/wheel/{id}/ and reconcile a 409 (another tab/session changed
// the wheel) instead of silently clobbering it, and (2) coalesce rapid save
// triggers into a single PUT. Pairs with the backend RENDERER_V2_SAVE_ENABLED
// Django env — the backend must ALSO be on for the 409 guard to engage (both
// flags are independent; the FE half is inert on its own). Touches the highest-
// blast-radius path in the app (every wheel edit) — see
// specs/post-bucket-d/renderer-v2-ladder.md §W3.
export const RENDERER_V2_SAVE_ENABLED = truthy(
  process.env.REACT_APP_RENDERER_V2_SAVE
);

// NEW_WHEEL_AUTOSAVE_ENABLED — EW-1262: a fresh/blank wheel never autosaves, so
// its first segments are lost on reload and Version History has nothing to
// snapshot. The builder's only autosave is a 30s setInterval whose effect deps
// include wheelData + the whole segmentBuilder slice, so every edit (add segment,
// auto-generate scheme) resets the 30s countdown to zero — a brand-new wheel is
// built through a burst of edits, so the timer never ticks and no wheel PUT ever
// fires. OFF (default) keeps the builder byte-identical to today: the prompt
// new-wheel save effect is never armed (the pure decision helper is short-
// circuited on `enabled`), and the only autosave remains the legacy 30s interval.
// ON schedules ONE prompt debounced save as soon as a not-yet-persisted blank
// wheel gains real content + unsaved changes, reusing the SAME existing autosave
// path (triggerAutosave -> PUT /api/v2/wheel/{id}/ — the identical update call an
// existing wheel already uses; NOT a new backend path). After the first
// successful persist the normal 30s interval governs. Scoped to blank-at-mount
// wheels only, so an existing wheel's save cadence is unchanged. Touches no
// money/auth path.
export const NEW_WHEEL_AUTOSAVE_ENABLED = truthy(
  process.env.REACT_APP_NEW_WHEEL_AUTOSAVE
);

// CAPTURE_ENABLED — Notepad / Capture inbox (web-first, ROAD-30 / SDD-21 / WL-021,
// backend #1182). OFF (default) keeps the app byte-identical: the /capture route
// is not registered and the Capture header entry is not rendered, so the surface
// is unreachable. ON registers the route + the flag-gated GalleryHeader "Capture"
// action. NOTE: the FRONTEND flag only reveals the UI — the BACKEND `CAPTURE_ENABLED`
// Django env must ALSO be true or every /capture/items/ endpoint 404s (both flags
// are independent; see ISP-4068). Mirrors the backend env.bool() convention.
export const CAPTURE_ENABLED = truthy(process.env.REACT_APP_CAPTURE_ENABLED);

// CHAT_ENABLED — Direct Messaging / DM web surface (WL-022, SDD-25/26,
// roadmap #22). OFF (default) keeps the /messages routes unregistered, so the
// whole chat surface is unreachable and byte-identical to today. Pairs with the
// backend CHAT_ENABLED Django setting (REST 404s while OFF). Flip-on is gated on
// Moderation (WL-030) + Notifications (WL-031) + Teams (WL-032) landing.
export const CHAT_ENABLED = truthy(process.env.REACT_APP_CHAT_ENABLED);

// CHAT_MEDIA — image attachments in private DMs (D22 / GR-5). OFF (default)
// keeps the composer text-only: no attach (paperclip) control is rendered, so
// there is no dead affordance. ON reveals the attach control, which routes the
// picked image through the SAME moderated upload wrapper as all uploads and
// (backend, pending) tallies the bytes against the UPLOADING user's storage
// quota so DM attachments accrue no free storage cost. Flip-on is gated on the
// backend Message-attachment field + storage-usage tally landing + deploy/QA.
export const CHAT_MEDIA_ENABLED = truthy(process.env.REACT_APP_CHAT_MEDIA);

// USER_ACTIONS_ENABLED — the shared Follow + Message affordance (UserActions
// component) rendered wherever ANOTHER user's avatar/name appears (search
// results, profile card, wheel-view owner byline, follower/following rows,
// creator cards). OFF (default) keeps every one of those surfaces
// byte-identical to today: the component returns null AND each wiring site is
// guarded (`{USER_ACTIONS_ENABLED && <UserActions/>}`, or a ternary that renders
// the surface's EXISTING follow button when OFF), so no new DOM, no follow-status
// fetch, and no DM network call is introduced while OFF. ON renders TWO
// independent controls — Follow (toggles the follow graph via
// /api/v1/user/{id}/follow|unfollow/) and Message (get-or-creates a 1:1 DM via
// POST /api/v1/dm/conversations/ then routes to /messages/{id}). The Message
// control is ITSELF additionally gated on CHAT_ENABLED (it has no destination
// while the DM surface is unregistered) — so with USER_ACTIONS on but CHAT off,
// only Follow appears. Both actions login-gate guests through the existing
// non-authenticated modal and are never shown on your own row. Reuses the
// backend follow + DM endpoints (no new BE); DM's own can_message policy
// (settings.DM_POLICY) still enforces server-side.
export const USER_ACTIONS_ENABLED = truthy(process.env.REACT_APP_USER_ACTIONS);

// PRESENCE_ENABLED — real online/offline presence for DM peers (BUG-chat-presence-
// all-offline, Kelly QA-146). OFF (default) because there is NO presence signal in
// the backend at all: PublicUserSerializer (messaging/api/v1/serializers.py) has no
// `is_online` field, and the only "presence" primitive anywhere is a per-WHEEL
// viewer roster (yuga_wheel/presence), unrelated to a DM peer's connectivity. So a
// literal "Offline"/"Online" label was a LIE — always "Offline", even for a peer
// who is online right now (validator wave34 measured this live, both directions).
// While OFF, the presence TEXT LABEL is withheld entirely (no misleading word),
// rather than inventing fake presence. Flip ON only once a real BE presence signal
// exists AND is surfaced on the DM peer payload — then the existing ternaries
// render the true state automatically. (The color-only presence dot is a separate
// a11y concern and is left as-is under this change.)
export const PRESENCE_ENABLED = truthy(process.env.REACT_APP_PRESENCE);

// AVATAR_WHEEL_FRAMES_ENABLED — Gordon law `all-avatars-wheel-frames` (2026-07-20,
// triggered by notification avatars rendering plain/broken): EVERY avatar in the
// app (a person's OR a wheel's) must render inside its "Wheel Frame" — the iYuga
// signature wheel-ring treatment (the canonical <UserImage> component, which lays
// the avatar photo inside ASSETS.backgroundWheelProfile) — never a bare/plain
// image, and the no-image fallback (initials OR placeholder) must render INSIDE
// the frame too. This flag converges the remaining UNFRAMED avatar render sites
// onto <UserImage>: the notification feed/dropdown actor avatar (the trigger),
// the account-switcher rows, the Referrals tables, and the DM surfaces
// (ConversationRow / Thread / MiniDmDock — whose deck-WL-022 tint+initials avatar
// is preserved, now drawn inside the frame with the presence dot kept un-clipped).
// The notification actor avatar additionally prefers the BE #1434
// `actor_profile_image` field (falling back to the legacy `profile_picture`) and
// uses `actor_user_id` to link the avatar to the actor's public gallery. OFF
// (default) keeps every one of those surfaces BYTE-IDENTICAL to today: each wiring
// site is a `AVATAR_WHEEL_FRAMES_ENABLED ? <UserImage/> : <existing JSX>` branch,
// so no DOM changes and the new BE fields are never read while OFF. Pure
// presentational convergence onto an existing shared component — touches no
// money/auth/network path (the avatar-link nav reuses the existing /gallery route).
// Staging merge-train env line: REACT_APP_AVATAR_WHEEL_FRAMES=true.
export const AVATAR_WHEEL_FRAMES_ENABLED = truthy(
  process.env.REACT_APP_AVATAR_WHEEL_FRAMES
);

// NOTIF_PLAIN_NAME_ENABLED — build the notification sentence from the BE #1434
// structured plain-text fields instead of parsing/rendering the untrusted HTML
// `description` string. ON: the actor name comes from the plain `actor_name`
// field wrapped in <strong> and the action phrase from the entity-DECODED,
// markup-flattened description (via inertHtmlToText) — so literal <p>/<b> tags
// never leak, a format_html-escaped apostrophe (O&#x27;Brien) renders as a real
// apostrophe, and a hostile display name is inert text (never innerHTML). OFF
// (default) keeps the existing renderSafeDescription path BYTE-IDENTICAL and never
// reads the new `actor_name` field. Presentational only — no money/auth/network
// path touched. Staging merge-train env line: REACT_APP_NOTIF_PLAIN_NAME=true.
export const NOTIF_PLAIN_NAME_ENABLED = truthy(
  process.env.REACT_APP_NOTIF_PLAIN_NAME
);

// GALLERY_FOLDERS_V2 — the "Drive-style" render-by-viewer gallery (ISP-4025) is
// now the ONLY gallery design; the flag + legacy Tabs path were removed
// (de-flag). Private-folder enforcement is server-side (BE #1188, merged).

// VIDEO_PORTRAIT_ENABLED — wheel-level Video/GIF Portrait authoring (WL-056 /
// DESIGN-DECISIONS-LOG rows 17/30/40). OFF (default) keeps the Wheel Settings ›
// Design › Portrait picker byte-identical to today: only a static image Portrait
// is offered, the Image | Video | GIF media-type toggle is not rendered, and no
// video/gif source tabs, upload-cap validation, or poster-frame picker appear.
// ON reveals the media-type toggle with tier-gated Video (ProSuite, ≤15s/≤25MB,
// poster fallback) and GIF (Essential, ≤8MB, seamless loop) options. The v2 wheel
// engine already RENDERS a video/gif portrait (src/renderer-v2 offscreen-pause +
// reduced-motion) — this flag only reveals the AUTHORING UI. Tier gating is
// ALSO enforced server-side (fail closed) — the client gate is UX only; pairs
// with the backend VIDEO_PORTRAIT_ENABLED Django setting. Touches no money/auth path.
export const VIDEO_PORTRAIT_ENABLED = truthy(
  process.env.REACT_APP_VIDEO_PORTRAIT
);

// MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED — WL-072 / EW-010. OFF (default) keeps
// the block-delete media cleanup byte-identical to today: BlockIndicator fires
// deleteWheelMedia for every media url the deleted block holds, with no
// refcount. ON refcounts each url against the rest of the CURRENT wheel's
// blocks (via craft's serialized node tree) and only releases a url that no
// surviving block still references — closing the same-wheel over-delete of
// shared media (EW-010) — and surfaces a user-facing error toast when a delete
// call fails instead of swallowing it to console. Cross-wheel refcount still
// needs a server-side counter (tracked in the register); this closes the
// common same-wheel case. Touches no money/auth path.
export const MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED = truthy(
  process.env.REACT_APP_MEDIA_LIFECYCLE_SAFE_DELETE
);

// GALLERY_EMPTY_DELIGHT_ENABLED — de-flagged (WL-058b cool-pass; deck
// concepts/gallery-elevated/04-A-empty-delight). The on-brand first-run Gallery
// empty state (brandWheelSVG sparkle ring, G-19 conformant) is now the ONLY
// design for the owner's Created-tab first-run empty; the flag + its OFF raster
// path for that surface were removed. Every other tab (Cloned/Unlocked/
// Favourited) and search-empty keep the existing explanatory raster icon
// (deliberate fallback, unchanged). See GalleryEmptyDelight + EmptyListView.

// WHEEL_TIME_MACHINE_ENABLED — the "time machine" scrubber view inside the
// Wheel Version History drawer (WL-043b). OFF (default) keeps the drawer
// byte-identical to today: only the timestamp LIST renders, no List | Time
// machine toggle is shown, and none of the scrubber / live-preview / diff code
// runs. ON adds a segmented toggle at the top of the drawer that swaps the list
// for a horizontal timeline SLIDER (WAI-ARIA slider: arrows + Home/End,
// >=44px thumb, live-region) whose position previews that saved version LIVE
// through the shared on-brand BrandWheel renderer (never a hand-rolled Wheel —
// gate G-19), with a plain-words diff of the selected version vs. now derived
// purely from the version payloads (no new network call — reuses the SAME
// useWheelVersions read). "Restore this version" routes through the drawer's
// EXISTING guarded confirm handler (restoring saves current state first —
// nothing is destroyed). Preview swap is GPU-only opacity and honours
// prefers-reduced-motion (instant swap). Touches no money/auth path.
export const WHEEL_TIME_MACHINE_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_TIME_MACHINE
);

// WHEEL_VERSION_HISTORY_ENABLED — the mount/entry-point flag for the Wheel
// Version History drawer (WL-043 / WL-043b / DL-039) in the Wheel Builder. The
// drawer + time-machine code shipped built-but-unmounted (PR #1806 + #1866); this
// flag is the mounting unit named in the bucket-D plan ("inert flags with no
// mount site — flip when their mounting unit lands"). OFF (default) keeps the
// builder BYTE-IDENTICAL to today: the "History" toolbar trigger is not rendered
// and the WheelVersionHistoryPanel is never mounted (no query, no drawer, no new
// DOM or state effect). ON renders a clock-icon "History" button in the builder
// SegmentActions toolbar (alongside Undo/Redo) that opens the right-side
// WheelVersionHistoryPanel — the deck's 3 states (history list / preview-restore /
// restored) faithful to design-handoff/concepts/wheel-history. The panel reuses
// the EXISTING useWheelVersions react-query read + guarded restore mutation (no
// new network path, no money/auth path). The nested "Time machine" scrubber view
// stays independently gated by WHEEL_TIME_MACHINE_ENABLED. Backed by the existing
// /api/v2/wheel/:id/versions/ endpoints (no BE change).
export const WHEEL_VERSION_HISTORY_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_VERSION_HISTORY
);

// EDIT_WHEEL_TUTORIAL_ENABLED — the mount/entry-point flag for the Edit-Wheel
// first-run guided tutorial tour (5-beat coach-tour, ISP-4045 / WL-044 follow-on,
// PR #1878 + media #1869; A9 in the CRACKS-REGISTER). The tour shipped MERGED but
// UNFLAGGED — mounted unconditionally on the builder — so it was silently live on
// every environment with no controlled-rollout gate and no tracking row. This flag
// retro-fits the codebase's default-OFF convention so the tour can be dark on prod
// and lit on staging (staging merge-train env line REACT_APP_EDIT_WHEEL_TUTORIAL=
// "true" so staging is never dark). OFF (default) keeps the builder BYTE-IDENTICAL
// to the pre-tour build: <EditWheelTutorial> is never mounted (no floating card, no
// coach mark, no signal reads, no localStorage tour record touched) and the Help
// page's "Take the builder tour" replay card is not rendered. ON restores exactly
// today's shipped behaviour — the non-blocking first-run tour (auto-starts once per
// user, skippable/resumable, replayable from Help; it only READS builder signals,
// never mutates the Wheel) plus the Help replay card. The ON code path is identical
// to PR #1878's unconditional mount, so flipping the flag ON reproduces the exact
// verified behaviour. Touches no money/auth/network path — pure presentational gating.
export const EDIT_WHEEL_TUTORIAL_ENABLED = truthy(
  process.env.REACT_APP_EDIT_WHEEL_TUTORIAL
);

// ENTERPRISE_PRICING_ENABLED — the sales-led "Enterprise" tier card on the
// pricing / subscription surface (specs/enterprise-tier, unit E4). OFF (default)
// keeps the pricing page byte-identical to today: only the four self-serve tiers
// returned by the subscriptions API render (Free / Essential / Premium / Pro
// Suite), and no Enterprise card is appended. ON appends a 5th "Enterprise" card
// — rendered through the SAME shared SubscriptionItem component (it is a data
// entry, not a hand-rolled fifth card) — with NO price shown, a "Let's talk"
// contact-sales CTA (a plain mailto: anchor to sales@iyuga.net; no checkout, no
// Stripe), and the governance feature bullets (SSO & auto-provisioning, org
// admin console, brand governance, audit logs, priority support & SLA, invoice /
// PO billing). Pairs with the backend ENTERPRISE_ENABLED tier seed (unit E1).
// Provisional and NOT sellable until the management pricing session.
export const ENTERPRISE_PRICING_ENABLED = truthy(
  process.env.REACT_APP_ENTERPRISE_PRICING
);

// WHEEL_CONTENT_SEARCH_ENABLED — Google-Docs-style in-Wheel CONTENT search on the
// public View Wheel page (ViewWheel, wave6). OFF (default) keeps the page
// byte-identical to today: the header "Quick Search" action is not rendered, the
// whole feature module (corpus + Range-API highlight walker + engine hook + bar)
// is NEVER mounted or fetched (lazy import behind the flag), and no wheel data,
// craft state, or DOM is touched. ON renders a floating search bar that indexes
// the open wheel's segment titles + text CLIENT-SIDE (no BE call), shows an N/M
// counter with next/prev (Enter / Shift+Enter), opens the matching segment via
// the SAME setWheelSegments selection path the wheel already uses, and
// inversion-highlights matches in the rendered DOM (true backdrop inversion with
// ENFORCED DISPARITY — when the inverted values land too close, lightness is
// pushed apart to ≥3:1 box / ≥4.5:1 text, hue kept; Gordon 2026-07-11 — plus a
// dual-signal halo) via Range-API text-node wrapping that renders straddled
// chunks as ONE continuous box and unwraps cleanly on close (Esc).
// DOM-decoration only; no money/auth path. MobileView UI + renderer-v2 port are
// explicit follow-ups (the engine is renderer-agnostic and ports later).
export const WHEEL_CONTENT_SEARCH_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_CONTENT_SEARCH
);

// WHEEL_PILL_LABELS_ENABLED — rim-hugging Segment-title PILL labels on the v1 D3
// wheel engine (src/components/D3Wheels/createPie.js), replacing the retired
// "spider-leg" leader-line labels (client-rejected 2026-07-06; recolour-alone was
// rejected — "the legs must go and the pills must hug the wedge rim"). OFF (default)
// keeps every wheel label BYTE-IDENTICAL to today: the leader-line + solid-fill
// rounded-rect ("spider-leg") label renders unchanged and NO pill code path runs.
// ON removes the leader legs and restyles each Segment-title label as a rim-hugging
// PILL per the design deck's canonical recipe — faint hue-tint fill + wedge-hue
// keyline stroke (NEVER solid) + DM Sans ink #123051 text — via colorsFor()
// (design-handoff/harness/elevations/segment-title-pill.mjs) applied through the
// same rim-hug geometry as ewflow-onbrand.mjs. Scoped to renders that actually SHOW
// labels (Edit wheel with no Segment selected; View wheel "Show labels" on);
// thumbnails / small wheels never enable labels, so they are untouched either way.
// Gates the v1 engine ONLY — the renderer-v2 port is a separate later step at
// migration. Touches no money/auth path.
export const WHEEL_PILL_LABELS_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_PILL_LABELS
);

// SEGMENT_COMPUTED_LABELS_ENABLED — compute unnamed-Segment placeholder labels
// ("Segment N") at RENDER time from the current wheel order, instead of reading
// the number baked into the stored title at creation (DL-029 / ISP-4018).
// OFF (default) keeps every label byte-identical to today: the wheel, the
// builder segment list and the title field all read the stored `title` verbatim,
// so a reordered wheel still shows the frozen creation numbers ("Segment 3,
// Segment 1, Segment 2"). ON routes every one of those read sites through the
// single shared utils/segmentLabels helper: a segment with a real user-given
// name keeps its name; an unnamed segment (empty title OR an exact baked
// "Segment N" placeholder) is labelled by its 1-based position among the UNNAMED
// segments only, in wheel order, so a reorder always renumbers 1..N with no gaps
// (named segments skipped). The title-edit write is also guarded so the shown
// placeholder is never persisted — only actually-typed names are saved. Pure
// frontend, no data migration: the follow-up BE title-nullable + backfill is a
// separate, dev-reviewed unit. Touches no money/auth path.
export const SEGMENT_COMPUTED_LABELS_ENABLED = truthy(
  process.env.REACT_APP_SEGMENT_COMPUTED_LABELS
);

// WHEEL_TITLE_PLAINTEXT_ENABLED — the ROOT fix for junk HTML in wheel titles
// (ISP-9999, pairs with #2125's display-strip). The builder "Wheel Name" field is a
// Froala rich-text editor (SegmentBuilder/Main/DetailsTab), so every title is stored
// with editor markup (e.g. "<p>Copy of X</p>") into what is meant to be a plain
// one-line title — the source of the <p>-in-titles complaint. OFF (default) keeps the
// DetailsTab title field BYTE-IDENTICAL to today: the FroalaTextEditor renders
// unchanged (rich toolbar, AI-title assist, HTML stored). ON swaps ONLY the title
// field for a plain <input> (the shared CustomInput, mirroring the CreateWheel "Wheel
// Title" input) that stores the raw STRING via the SAME setWheelName + history path —
// no HTML wrapper is ever produced for a title again. Subtitle and description keep
// their Froala editors. Preserves the field's 75-char limit, "Untitled Wheel"
// placeholder, "Wheel Name" label, and the click-title-in-header -> focus-field
// behaviour. Pairs with the backend wheel_name save-sanitize (defensive, all write
// paths) and the one-time cleanup management command (existing junk titles). Flip ON
// only AFTER the cleanup command has run, so existing HTML titles are already plain
// text and never surface as raw markup in the input. Touches no money/auth/network
// path — the stored field type is unchanged (same wheel_name string).
export const WHEEL_TITLE_PLAINTEXT_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_TITLE_PLAINTEXT
);

// PLAIN_SPEECH_INTROS_ENABLED — DL-003 plain-speech documentation mandate
// (ELEVATED-DESIGN-LANGUAGE.md §10, notes6 amended notes7). OFF (default) keeps
// every admin/advanced screen byte-identical to today: the shared
// <PlainSpeechIntro> and <PlainSpeechRow> components render nothing (return
// null), so no "What is this page?" strip and no grey-italic explainers are
// added to the DOM. ON renders, on each wired admin/advanced surface, a short
// plain-language "What is this page?" intro at the top plus a one-line
// grey-italic (11–13px) explainer under each config row — a bare sentence with
// NO label prefix (notes7). Purely additive, non-technical copy that reuses the
// app's own vocabulary (audits/LANGUAGE-AUDIT.md); money/legal wording is left
// untouched. Touches no money/auth/network path. Management's copy-review
// console reviews the wording before this flag is flipped on.
export const PLAIN_SPEECH_INTROS_ENABLED = truthy(
  process.env.REACT_APP_PLAIN_SPEECH_INTROS
);

// SEGMENT_STYLING_V2_ENABLED — the WL-012 redesigned per-Segment "Segment
// Styling" pane (SegmentStylingPane -> SegmentFillPanel fill pills: Color /
// Gradient / Pattern / Image / GIF, plus the icon + brand-palette editors).
// This RE-INTRODUCES the gate that was removed on 2026-07-09 ("de-flagged"),
// which had shipped the new pane as the ONLY design on staging while its
// Pattern / Image / GIF sub-editors were still unbuilt stubs (clicking those
// pills opens no picker — SEGMENT-GIF-FILL-TRUTH 2026-07-13). OFF (default)
// keeps the Segment Settings pane byte-identical to the pre-de-flag design:
// the new SegmentStylingPane is NOT mounted anywhere, so ONLY the existing,
// WORKING legacy Segment fill controls render — the "Wallpaper" accordion
// (Colors / Gradient / Image / SVG Patterns) inside <SegmentSettings> and the
// resize controls in SegmentsTab — and the incomplete new pills are hidden.
// Color/Gradient in the new pane DO work, but Pattern/Image/GIF are dead
// stubs, so hiding the whole new pane is what restores a fully-working fill
// experience for the demo. ON re-mounts the new pane at both call sites
// (SegmentSetting auto-open pane + SegmentsTab Design > Segments) exactly as
// it renders today — flip ON only once the Pattern/Image/GIF pickers exist.
// End-state (separate, later): once the new pickers are built and this flag is
// ON, the legacy accordion is removed (no-outdated-designs). Touches no
// money/auth/network path — pure presentational gating.
export const SEGMENT_STYLING_V2_ENABLED = truthy(
  process.env.REACT_APP_SEGMENT_STYLING_V2
);

// SEGMENT_FILL_UNIFIED_ENABLED — u6-segment-fill: fold the segment side pane's
// separate "Segment Color" section AND the fill-type editor into ONE section
// named "Segment Fill" (the color/design of the Segment FRAME), matching the
// Edit-Wheel-Flow deck (ewflow-segment-fills, states 75x): one accordion with
// type tabs Color / Gradient / Pattern / Image / GIF. This is UI RE-HOUSING
// ONLY — it mounts the EXISTING controls (the rich <SegmentColor> palette for
// the Color tab, the WL-012 SegmentFillPanel pickers + FocalZoomControl for
// Pattern/Image/GIF, and GradientBuilderPanel for Gradient) and changes NO
// data model: the color path still writes `settings.color`, and the media/
// pattern path still writes `settings.fill` (+ the legacy currentBg/bgWallpaper
// fields via legacyFieldsForFill, + the snake_case model fields when
// SEGMENT_ARC_FILLS is ON) exactly as before. The Wallpaper section (the open-
// Segment CANVAS background, where Blocks live) is OUT OF SCOPE and byte-
// untouched. OFF (default) keeps the pane byte-identical to today: the stand-
// alone "Segment Color" accordion renders as now and no "Segment Fill" section
// is added; the old WL-012 fill pane (SEGMENT_STYLING_V2) is unaffected. ON
// renders the unified "Segment Fill" section, hides the stand-alone "Segment
// Color" accordion, and hides the old WL-012 fill pane so only one fill UI
// shows. Touches no money/auth/network path — pure presentational gating.
export const SEGMENT_FILL_UNIFIED_ENABLED = truthy(
  process.env.REACT_APP_SEGMENT_FILL_UNIFIED
);

// PATTERN_BG_ENABLED — the "pattern background LAW" upgrade to the System-A
// parametric SVG pattern picker (BackgroundPatternPanel — the ONE component behind
// the SegmentFillPanel Pattern tab, the Wallpaper "SVG Patterns" category, and the
// Accordion header Patterns tab). Gordon's ruling (memory pattern-background-color-
// ruling.md): a pattern render is ALWAYS >=2 colors — #1 line-art color + #2
// background color, BOTH chosen from the app's STANDARD color sources (Preset /
// Custom / Wheel / Segment), never a raw <input type=color>; an explicit
// "transparent" background is a valid choice shown as a red circle-with-slash swatch
// (implicit transparency = defect). OFF (default) keeps BackgroundPatternPanel
// BYTE-IDENTICAL to today: the two native <input type="color"> Foreground/Background
// controls render, no standard-sources picker or transparent swatch mounts, and no
// redux/palette hook runs for the pattern colors. ON swaps those two raw inputs for
// two composed <SegmentColor> standard-source pickers (line-art + background) plus a
// shared TransparentSwatch offered in the BACKGROUND choices only (the line-art color
// must always resolve to a real color), seeds the default pair from the segment's own
// fill color (art) + white (bg), and persists the ordered colors:[art,bg] list.
// Colors are still baked into the SAME bg_wallpaper CSS string (no schema change, no
// migration) — legacy {fg,bg} data reads as [fg,bg] via a migration-free adapter.
// SCOPE: System A only — the 28 legacy line-art wallpaper patterns (System B) are a
// separately-sequenced unit that reconciles with the perf-phase1 CSS-mask model.
// Touches no money/auth/network path — pure presentational gating.
export const PATTERN_BG_ENABLED = truthy(process.env.REACT_APP_PATTERN_BG);

// TRANSPARENT_PRESET_ENABLED — Gordon 2026-07-20: the red circle-with-slash
// "Transparent" swatch (the shared <TransparentSwatch>, first introduced by
// PATTERN_BG) should be offered as an explicit choice in EVERY "preset color"
// option across the app — EXCEPT the wheel's own anatomy and a few
// invisible-content controls. It is NEVER the default/pre-selected value.
// OFF (default) keeps every preset-color picker BYTE-IDENTICAL to today: no
// TransparentSwatch is inserted into any preset row, the grid item count and
// layout are unchanged, and no new value is ever emitted. ON inserts the shared
// swatch into the SOLID preset row of the content-block color picker
// (<ColorPopup>, allowTransparent default true) so a block background / border /
// shadow / fill / icon can be deliberately transparent; selecting it emits the
// literal CSS value "transparent" through the SAME onChange the presets already
// use (getPaletteColor passes "transparent" through verbatim — no schema change,
// no migration). Per-context opt-out prop `allowTransparent={false}` withholds
// the swatch on the excluded surfaces. The wheel-anatomy picker (<SegmentColor>)
// defaults allowTransparent=false, so Segment Color / Segment Fill / Segment &
// Wheel Wallpaper never show it even when the flag is ON (Gordon's binding
// exclusion list). Invisible-content controls (text/font colors, pattern
// line-art) and the Palette Generator are excluded too. Touches no
// money/auth/network path — pure presentational gating.
export const TRANSPARENT_PRESET_ENABLED = truthy(
  process.env.REACT_APP_TRANSPARENT_PRESET
);

// INK_TOKEN_SWEEP_ENABLED — re-tokens the base body TEXT color at the shared
// root (audit 2026-07-13 Y1 / DL-021). Bootstrap's reboot sets the <body> color
// to its $body-color default (#212529), and src/index.scss never overrides it,
// so all base/inherited text (page body, gallery card titles, Business-Box
// heading, ShareModal header, etc.) computes to Bootstrap ink #212529 instead of
// the brand ink $blue #123051. OFF (default) keeps the app byte-identical to
// today: no `ink-token-sweep` class is added to <body>, so the inert
// `body.ink-token-sweep { color: $blue }` rule in index.scss never matches and
// the reboot #212529 is untouched. ON adds the class in index.js, retokening ONLY
// the inherited base text color to #123051 at the shared root — explicit colors
// (intentional greys, disabled states, headings already #123051, any element that
// sets its own color) are unaffected because they override inheritance. Touches
// no money/auth/network path; no per-component edits.
export const INK_TOKEN_SWEEP_ENABLED = truthy(
  process.env.REACT_APP_INK_TOKEN_SWEEP
);

// EXPLORE_WHEEL_CARD_ART_ENABLED — full Wheel art (segments + wallpaper) on the
// ExploreV2 public-discovery-feed cards, instead of the DL-002 logo-only center
// (owner decision Gordon 2026-07-13: "full Wheel EVERYWHERE", overriding DL-002
// for the Explore cards). OFF (default) keeps every Explore card byte-identical
// to today: the card shows the `wheel_image` raster preview when present, else
// the iYuga-logo fallback — the new data-driven-Wheel branch is never taken. ON
// renders the canonical shared Wheel (WheelComponent + the gallery's
// CardWallpaperBackground) whenever the card's wheel data carries a non-empty
// `wheel_segments` array; when segment data is absent (e.g. the BE
// WheelExploreSerializer has not yet added wheel_segments / wallpaper_settings)
// the card degrades gracefully to the SAME logo-only / raster fallback as OFF —
// never a bare or hollow center. Pure presentational gating: touches no
// money/auth/network path. Pairs with the separately-requested BE change that
// adds wheel_segments (+ wallpaper_settings) to the Explore feed serializer.
export const EXPLORE_WHEEL_CARD_ART_ENABLED = truthy(
  process.env.REACT_APP_EXPLORE_WHEEL_CARD_ART
);

// SHARED_CLONE_RETURN_ENABLED — QA-148 #6 web parts: connect the shared-Wheel ->
// clone journey (Shared Wheel -> View Wheel -> Get App / Sign Up -> auto-return
// to the SAME Wheel -> obvious "Clone this Wheel" CTA). OFF (default) keeps the
// flow BYTE-IDENTICAL to today: the ViewWheel guest-clone dialog still does a
// bare `nav("/login")`, Signup still lands on `nav("/gallery")`, and no clone-CTA
// banner is mounted — utils/sharedCloneReturn is never called, so nothing is
// written to localStorage and no new DOM appears. ON (1) remembers the origin
// Wheel (path + wheel id, 6h TTL localStorage) when a guest leaves View Wheel to
// authenticate intending to clone, (2) sends the visitor back to that SAME Wheel
// after login OR signup (login already carries `?redirect=`; this survives the
// signup + email-verify detour the redirect param cannot), and (3) renders an
// obvious "Clone this Wheel" CTA banner on the returned Wheel. Reuses the
// existing clone action (handleCloneWheel) and the existing same-origin redirect
// guard (safeLoginRedirectTarget). Touches no money path; the auth redirect
// target is still passed through the same open-redirect guard. Staging merge-train
// env line: REACT_APP_SHARED_CLONE_RETURN=true. The native-app deep-link /
// attribution half of QA-148 #6 is React Native and out of scope here.
export const SHARED_CLONE_RETURN_ENABLED = truthy(
  process.env.REACT_APP_SHARED_CLONE_RETURN
);

// DISCOVER_CARD_V2_ENABLED — the consolidated Discover wheel-card redesign
// (QA-116, owner-approved spec specs/discover-card/CARD-REDESIGN-SPEC.md,
// Gordon 2026-08-13 live QA). OFF (default) keeps every Discover card
// byte-identical to today: the "Following" state shows as the on-wheel badge,
// no favorite-heart control, and the current spacing/avatar. ON turns the card
// "alive" and correct:
//   • the "Following" state moves OFF the wheel image to a chip beside the
//     CREATOR NAME (you follow the creator, not the wheel) (BUG-discover-f6f307);
//   • an interactive favorite HEART (hollow -> fills red, white contrast ring)
//     bound to wheel.is_favourite sits top-right over the preview;
//   • tighter vertical text spacing + a larger creator avatar.
// The from_followed feed RANKING (useExploreFeed interleave, Kelly QA-17) is NOT
// touched by this flag. Purely presentational gating — no money/auth path.
// NOTE (BUG #927f): video-portrait playback (wheel_center_media.type ===
// "video" shows its POSTER still and plays the muted/looping clip on hover,
// instead of the default iYuga logo — BUG-explore-10996e) is gated on
// VIDEO_PORTRAIT_ENABLED instead, independent of this flag — it must keep
// working the same way it already does on View Wheel/Gallery regardless of
// whether the rest of this card redesign has shipped.
export const DISCOVER_CARD_V2_ENABLED = truthy(
  process.env.REACT_APP_DISCOVER_CARD_V2
);

// GALLERY_ARRANGE_ENABLED — the owner's drag-to-rearrange gallery + 6 sort views
// (WL gallery-arrangement). OFF (default) keeps the Gallery byte-identical to
// today: NO "Organize" toggle and NO "Sort" control render, the Created grid uses
// the plain WheelCard grid with zero drag/selection, and no reorder endpoint is
// ever called. ON adds (a) a Sort dropdown (owner AND guest — 6 read-only
// re-sorts: Recency/Alphabetical/Views/Clones/Favorites/Unlocks, default = the
// owner's custom order) wired to the gallery list's sort params, and (b) for the
// OWNER on their own Created tab, an "Organize" mode: multi-select (shift/ctrl
// click + marquee), deck-of-cards drag, a vertical insertion line, and a
// persisted custom order. Reorder is OWNER-ONLY and enforced SERVER-SIDE (the
// backend reorder endpoint 403s a non-owner) — the client gate is UX only. Pairs
// with the backend gallery_position field + POST /api/v2/wheel/reorder/ + the
// list-endpoint sort params. Guests can sort but never see the Organize surface.
// Staging merge-train env line: REACT_APP_GALLERY_ARRANGE=true.
export const GALLERY_ARRANGE_ENABLED = truthy(
  process.env.REACT_APP_GALLERY_ARRANGE
);

// PAID_LOCKS_ENABLED — wheel-level paid unlock (WL-042 / BE feature #12, PR #1181).
// A creator (ProSuite only) sets a one-time price on a Wheel; a guest pays via
// hosted Stripe Checkout to unlock durable, per-person access. OFF (default) keeps
// the app BYTE-IDENTICAL to today: the ProSuite tier-card bullet stays "Paid Locks
// (Coming Soon)", the Create-Wheel lock tooltip keeps "(coming soon)", the Lock
// Settings modal renders NO Paid Access pane, and ViewWheel never calls the
// paywall / access / purchase-intent endpoints (no teaser, no redirect, no
// return-URL handler). ON reveals the owner Paid Access config (gated ALSO by the
// server + ProSuite tier) and the guest paywall spine. This client flag pairs with
// the backend PAYMENT_LOCKING_ENABLED Django setting (default False): BOTH must be
// true for the buy flow to work — with the BE flag OFF, `access/` returns
// {feature_enabled:false} and the UI stays inert (fail-safe). Money path — never
// flip to prod without dev + Gordon sign-off (#1181's own banner).
//
// The Create-Wheel lock tooltip (WheelLocking/index.jsx) is ALSO server-gate-aware:
// even with this client flag ON, it probes the same `access/` read (wheelId 0 —
// no real wheel exists yet at that step) and only drops "(coming soon)" once the
// server confirms `feature_enabled: true`. So a FE-ON / BE-OFF environment still
// shows "(coming soon)" — the tooltip never promises Payment before the server
// backs it (ISP-9999 delta-QA fix).
export const PAID_LOCKS_ENABLED = truthy(process.env.REACT_APP_PAID_LOCKS);

// DATA_ENTRY_LOCK_ENABLED — Data-Entry (quiz-as-unlock) lock, Phase 1 MVP (mirrors BE
// DATA_ENTRY_LOCK_ENABLED). A standalone lock method: the creator builds auto-gradable
// questions (multiple-choice / true-false / fill-blank) + a pass threshold in the Lock
// Settings modal; a visitor answers them on the locked-wheel gate and, on meeting the
// threshold, the wheel unlocks via the SAME grant the password/email/paid flow uses.
// OFF (default) keeps every surface byte-identical to today: no Data-Entry accordion in
// LockSettingsModal, no quiz modal on the view gate, and no data-entry network calls.
// Deploy DARK; flip ON only after live QA on web-stg (its BE half must be live first).
export const DATA_ENTRY_LOCK_ENABLED = truthy(
  process.env.REACT_APP_DATA_ENTRY_LOCK
);

// AI_FUNNEL_ENABLED — the staged AI Wheel Builder funnel (D14; deck
// design-handoff/tabs/ai-builder.html + concepts/ai-builder/*).
// WHAT IS BUILT TODAY (the honest FE spine): choose (describe / from-website)
//   -> website (URL -> SSRF-guarded scrape) -> scope (edit extracted text)
//   -> the EXISTING one-shot <NewAIBuilder> modal, seeded with that text.
// NOT YET BUILT (deck-committed, tracked in the Design-Lock Ledger / UNBUILT
//   backlog, do NOT read this comment as "done"): persistent mode tabs +
//   "From your photos", crawl page-selection, the Segments & Cost review stage
//   (02c/02f) and its server estimate, and the 5 worked-example wheels. The
//   aiFunnelEstimateCost API method is wired but UNCALLED until that stage lands.
// OFF (default) keeps the AI builder BYTE-IDENTICAL to today: the existing
// one-shot <NewAIBuilder> modal renders unchanged and none of the funnel stages
// or website-scrape network calls exist. ON reveals the staged funnel. Pairs
// with the backend AI_FUNNEL_ENABLED Django setting — BOTH must be true for the
// scrape endpoint to respond (it 404s while the BE flag is OFF; the two flags
// are independent, the FE funnel is inert without the BE). Money path (token
// purchase) reuses the EXISTING token-bundle flow unchanged — never flip to prod
// without dev + Gordon sign-off.
export const AI_FUNNEL_ENABLED = truthy(process.env.REACT_APP_AI_FUNNEL);

// PDF_PREVIEW_STYLES_ENABLED — DL-049 "Full / Cover" document preview styles on
// the Document content block (pdf-viewer-elevated deck, 02-view-selector-sidepane).
// OFF (default) keeps the block BYTE-IDENTICAL to today: the on-wheel render is
// the existing icon + filename row ("Icon List"), and the block's Layout settings
// tab shows no preview-style control. ON adds (a) a "Show preview" switch + the
// canonical THREE-way "Preview style" segmented control [Full | Cover | Icon List]
// + a "Preview page" dropdown (dimmed for Icon List) in the Layout tab, and
// (b) two new on-wheel render modes — Full (first-page preview card) and Cover
// (lavender cover-style card) — that reuse the already-shipped in-app viewer
// (PdfFirstPagePreview renders the real first page; PdfViewerModal is the in-app
// Open; isPdfUrl gates PDF vs. the legacy open-externally path). "Icon List" is
// the app's EXISTING icon+filename render, shown untouched — it routes through
// the SAME byte-identical row render as the flag-OFF default (no new render path).
// Non-PDF files gracefully fall back to their type icon inside the card.
// Client-only: renders ALREADY-loaded document props, makes no new network
// contract, and touches no money/auth path. Mirrors the backend env.bool()
// default-OFF convention.
export const PDF_PREVIEW_STYLES_ENABLED = truthy(
  process.env.REACT_APP_PDF_PREVIEW_STYLES
);

// CAROUSEL_BLOCK_ENABLED — the DL-041 "film-strip" Image Carousel content block
// (LOCKED deck spec: concepts/carousel-block-proposal). A new craft.js block that
// cycles through several images as a gapped, gradient-bordered film strip — each
// image keeps its own shape at a shared height (no crop), with manual arrows /
// dots / swipe by default and an optional Auto mode (pauses off-screen, honours
// reduced-motion). OFF (default) keeps the builder byte-identical to today: the
// "Carousel" tool is NOT appended to the Content toolbox group, so the block can
// never be added and no carousel node exists to render. The block's resolver
// entry + code export are inert without a node (forward-safe: a carousel saved
// while ON still deserializes if the flag is later turned OFF, rather than
// throwing). Live wheels use the legacy react-multi-carousel elsewhere; this adds
// a first-class authorable block and touches no money/auth/network path.
export const CAROUSEL_BLOCK_ENABLED = truthy(
  process.env.REACT_APP_CAROUSEL_BLOCK
);

// CONTAINER_BACKGROUNDS_ENABLED — the Container block's Background control gains a
// 3-type selector (Image / GIF · Color · Gradient), replacing the color-only
// "Background Fill" (Gordon 2026-07-06; deck concepts/edit-wheel-flow/44a-44b).
// OFF (default) keeps the Container settings pane byte-identical to today: the
// Basic section renders the EXACT existing <ColorField label="Background Fill">
// (native color + opacity + reset, dead Solid|Gradient depth tabs), the renderer
// uses its unchanged `applyOpacity(getPaletteColor(background), …)` solid-color
// path, and NO new craftjs node prop is written. ON swaps the Basic control for
// <ContainerBackgroundControl>: a real ARIA type selector where Color keeps the
// existing ColorField, Gradient mounts the app's REAL gradient builder
// (Shared/SegmentStyling/GradientBuilderPanel) and Image / GIF mounts the
// universal image picker (Shared/MediaSelection, isGifAllowed) — the SAME surfaces
// the Segment fills use (reuse, not a second UI). The chosen fill persists as a
// new `containerFill` node prop through the EXISTING wheel-content save path (no
// BE change, no migration; it is arbitrary JSON on the craft node) and the
// renderer paints it via the canonical buildSegmentFillCss. A plain solid-color
// container (existing wheels, or the Color type) still takes the unchanged legacy
// path, so ON is additive. Touches no money/auth/network path.
export const CONTAINER_BACKGROUNDS_ENABLED = truthy(
  process.env.REACT_APP_CONTAINER_BACKGROUNDS
);

// DIVIDER_SVG_STYLES_V3_ENABLED — the expanded SVG divider-style set (DL-042,
// bucket-D wave D1; deck concepts/divider-styles-proposal/index.html v3 +
// baked gradient-fixed port in ewflow-picker-text.elevation.mjs). The Divider
// content block ships 4 CSS border styles today (solid/dashed/dotted/double).
// OFF (default) keeps the Divider block + its style picker BYTE-IDENTICAL to
// today: the Shared/DividerStyles picker renders exactly its current 4-box CSS
// row, the block renders via the existing CSS border-top path, and none of the
// SVG library, family-grid picker, animation/flip controls, or new craft props
// (svgStyle/animate/animateDir/flip) are read or rendered. ON swaps the picker
// for a family-grouped, scrollable grid of 37 crisp inline-SVG divider styles
// (7 families: basic/dots/waves/geo/botanic/ornament/special) and renders the
// selected style as an inline SVG in the block (via the pure builder in
// Shared/DividerStyles/svgDividers.js — all geometry generated in-code, color
// sanitized before injection), honoring the existing Thickness / Color /
// Transparency / Margin controls plus opt-in per-divider Animate (left/right
// drift, CSS-only, frozen under prefers-reduced-motion) and Flip for
// asymmetric styles. Pre-existing dividers (svgStyle unset) keep the legacy CSS
// render even while ON. Pure presentational gating: touches no money/auth/
// network path.
export const DIVIDER_SVG_STYLES_V3_ENABLED = truthy(
  process.env.REACT_APP_DIVIDER_SVG_STYLES_V3
);

// ACCORDION_HEADER_BACKGROUNDS_ENABLED — the Accordion block's "Title Background"
// control gains the deck's 4-type fill selector (Colors · Gradient · Patterns ·
// Image · GIF) plus auto-contrast title tone, replacing the color-only ColorPopup
// (DL-037 / bucket-D wave D2; deck concepts/builder-blocks/09-accordion-header-bg-editor
// "N39" + 10-viewwheel-accordion-headers). OFF (default) keeps the Accordion
// settings pane BYTE-IDENTICAL to today: the legacy <ColorPopup title="Title
// Background"> renders (native color + opacity + suggested palette), the renderer
// uses its unchanged headerBgColor path, the header title stays always-white, and
// NO new craft node prop (headerFill / headerTitleAutoContrast / applyToAllHeaders)
// is written. ON swaps the ColorPopup for <AccordionHeaderBackgroundControl>: the
// Colors tab keeps the same headerBgColor + opacity, Gradient mounts the app's REAL
// gradient builder, Patterns mounts the existing BackgroundPatternPanel, and Image
// mounts the universal media picker (Upload / Browse / Recent) — animated GIF
// headers are an Essential-tier feature (sub_tier >= 1), stills otherwise. The
// chosen fill persists as a new `headerFill` node prop through the EXISTING wheel-
// content save path (no BE change, no migration; arbitrary JSON on the craft node)
// and the renderer paints it via the canonical buildSegmentFillCss. Auto-contrast
// flips the title white / navy by the background's luminance (the Segment-icon
// rule). "Apply to all Accordion headers in this Segment" copies the fill to
// sibling accordions. A plain solid-color header (existing accordions, or the
// Colors tab) still takes the unchanged legacy path, so ON is additive. Touches no
// money/auth/network path.
export const ACCORDION_HEADER_BACKGROUNDS_ENABLED = truthy(
  process.env.REACT_APP_ACCORDION_HEADER_BACKGROUNDS
);

// IMAGE_CAPTION_OVERLAY_ENABLED — the Image content block gains a "Text overlay"
// section: a caption placed on one of 9 spots of a 3×3 grid, an overlay scope
// (None / Whole image / Behind text) with a Darken↔Lighten amount, and a text tone
// (Auto / Light / Dark) with an AA readable-contrast guard over arbitrary images
// (DL-037 / bucket-D wave D2; deck concepts/builder-blocks/11-image-caption-overlay-editor
// "N40" + 12-viewwheel-image-captions). OFF (default) keeps the Image editor + the
// rendered Image BYTE-IDENTICAL to today: the WL-011 ImageEditor renders no Text
// overlay section, the wiring writes NO `captionOverlay` node prop, and ImageBlock
// paints exactly its current output (the existing "Display Caption" bottom bar is
// untouched). ON adds the Text overlay section (a shared control that also styles
// Container title bars, N41) and the renderer paints the caption as an absolutely-
// positioned overlay via the pure Shared/CaptionOverlay model (position + scrim +
// auto tone). The overlay persists as arbitrary JSON on the craft node (no BE
// change, no migration). A saved overlay still renders if the flag is later turned
// OFF (forward-safe). Touches no money/auth/network path.
export const IMAGE_CAPTION_OVERLAY_ENABLED = truthy(
  process.env.REACT_APP_IMAGE_CAPTION_OVERLAY
);

// NOTIFICATION_PREFERENCES_ENABLED — Settings › Notifications preference panel
// (DL-053; deck concepts/notification-center-elevated/04-preferences-inapp-only.html,
// index.md N23). OFF (default) keeps Settings byte-identical to today: the
// "Notifications" tab is NOT inserted into the Settings options map, so its
// sidebar entry and panel never render and the preferences endpoint is never
// called. ON adds a "Notifications" tab whose panel shows per-category In-app
// toggles (Social, Team editable; Moderation, Account & Billing always-on and
// disabled), persisting only the editable In-app booleans via
// GET/PATCH /api/v1/notification/preferences/. Deck governs IN-APP ONLY — the
// Email channel is on HOLD (decision #32, per-send cost); no Email column is
// rendered. Pairs with the backend NOTIFICATION_PREFERENCES_ENABLED Django
// setting (the endpoint 404s while that is OFF — both flags are independent).
// Touches no money/auth path.
export const NOTIFICATION_PREFERENCES_ENABLED = truthy(
  process.env.REACT_APP_NOTIFICATION_PREFERENCES
);

// TABLE_BLOCK_ENABLED — the NEW Table content block (Build-Menu Content group,
// ISP-4018; deck concepts/builder-blocks/02-table-block-editor). Mirrors the
// DL-041 Carousel gate so the block's ONLY user-facing surface — the
// constants/segmentTools.js Content-toolbox entry — is guarded by a single
// conditional spread:
//     ...(TABLE_BLOCK_ENABLED ? [ {title:"Table", dataCy:"toolbox-table", ...} ] : [])
// OFF (default) keeps the Build Menu byte-identical to a build without the block:
// the Table card is not rendered and the block cannot be added. The craft.js
// resolver keeps TableWidget registered unconditionally, so any Table nodes in
// already-saved wheels still render read-only in the editor and published view.
// ON adds the card; the block, editor (TableEditor), pure grid ops (Table/
// wiring.js) and accessible <table> render (TableView) are unchanged either way.
// Pairs with the staging merge-train env line REACT_APP_TABLE_BLOCK=true so
// staging is never dark. Touches no money/auth/network path.
export const TABLE_BLOCK_ENABLED = truthy(process.env.REACT_APP_TABLE_BLOCK);

// PREVIEW_CONTEXT_RESTORE_ENABLED — restore the segment you were editing (plus the
// open builder pane) after a Preview round-trip. Today, Builder › "View Wheel"
// (Preview) navigates to /wheel/:id; pressing browser Back re-mounts the builder,
// whose unmount already dispatched resetBuilder(), so getWheelData re-fetches with
// no `selected` segment and the generic (nothing-open) state is shown. OFF (default)
// keeps every path BYTE-IDENTICAL to today: the Preview button stamps nothing on the
// URL and getWheelData keeps its existing `selected: position === selectedArc?.position`
// mapping + default `selectedMenu`, so a Back after Preview lands on the generic state
// exactly as now. ON stamps the open segment id + pane onto the builder history entry
// via window.history.replaceState (the "router state" pattern already used at
// SegmentBuilder/index.jsx for /builder/:id) so the browser Back button carries them
// back; getWheelData then re-selects that segment and re-opens that pane. If the
// segment was deleted while previewing, the restore is a graceful no-op (generic
// state, no crash). Pure client-side UX state restoration — touches no data / auth /
// permission / network path, adds no request. Adopts + supersedes DRAFT PR #1770
// (rebased to sandbox, flag-gated, adds pane + not-found fallback + tests).
export const PREVIEW_CONTEXT_RESTORE_ENABLED = truthy(
  process.env.REACT_APP_PREVIEW_CONTEXT_RESTORE
);

// POLL_LIVE_VOTING_ENABLED — wires the ALREADY-SHIPPED Poll content block
// (SegmentBuilder/CodeBlocks/Poll, Wave-5 Lane-N BLOCK-FORMS) to real voting in
// the published wheel view (Bucket-D Wave-D2). The Poll block authoring surface
// + builder preview already exist and are UNCHANGED by this flag. OFF (default)
// keeps the block BYTE-IDENTICAL to today everywhere: in the builder AND in
// ViewWheel it renders its local preview tally, the "Vote" button only sets a
// local `voted` flag, and NO poll network request is ever made. ON adds a LIVE
// path that only activates in the published view (craft Editor enabled === false)
// when a wheelId is present: the "Vote" button POSTs to the existing endpoint
// /wheel/forms/poll/<wheel_id>/<block_id>/vote/ and results come from
// /wheel/forms/poll/<wheel_id>/<block_id>/results/ (services/pollBlocks). One vote
// per user/session is enforced SERVER-side (single-choice replaces the prior
// vote; the block sends allow_multiple) — the client only keeps a localStorage
// marker to show standings on return. Those endpoints are Premium-gated on the
// wheel OWNER's tier server-side (fail closed) so the UI degrades gracefully on a
// 403. No new backend, no migration, no parallel endpoints. Touches no money/auth
// path. block_id is the poll's craft.js node id.
export const POLL_LIVE_VOTING_ENABLED = truthy(
  process.env.REACT_APP_POLL_LIVE_VOTING
);

// TITLE_BAR_BLOCK_ENABLED — the Title Bar content block (Bucket D / wave D2; deck
// concepts/builder-blocks/15-titlebar-block-editor.html, round-7 v3, supersedes
// 14/13). A new craft.js block: a standalone plain-rectangle HEADING BAND
// spanning the Segment content width (2px corners, no chevron/pill, nothing
// expands — deck state 14) with a single heading whose size / font / alignment /
// tone the author chooses, and a background REUSED from the Container block's
// #1996 picker (Image/GIF · Color · Gradient). OFF (default) keeps the builder
// BYTE-IDENTICAL to today: the "Title Bar" tool is NOT appended to the Content
// toolbox group, so the block can never be added and no title-bar node exists to
// render. The block's resolver entry + code export are inert without a node
// (forward-safe: a title bar saved while ON still deserializes if the flag is
// later turned OFF, rather than throwing). Typography comes from tokens only; the
// title auto-flips light/dark by background luminance for AA legibility. Touches
// no money / auth / network path.
export const TITLE_BAR_BLOCK_ENABLED = truthy(
  process.env.REACT_APP_TITLE_BAR_BLOCK
);

// TITLE_BAR_DECK15_ENABLED — the DECK-15 DELTA controls for the Title Bar block:
// the control groups the shipped "smallest block" scope left DESIGNED-not-built
// in the canonical deck (concepts/builder-blocks/15-titlebar-block-editor.html,
// round-7 v3) — Corner rounding None·S·M·Full, Width Full·75%·50%, the 3x3 Text
// position grid (the built Left/Center/Right is its 1-D subset), Overlay scope
// (Block behind text · Shadow behind text · Whole bar) with the Darken<->Lighten
// slider, and the "Custom" escape hatch on Height AND Text size. OFF (default)
// keeps the Title Bar BYTE-IDENTICAL to today: the settings pane renders exactly
// its shipped Heading/Bar/Advanced fields (no new control mounts), the block
// writes no new props, and the band renders with the shipped 2px corners, full
// width, centered heading and no overlay — a title bar authored while ON simply
// ignores the extra props when the flag is later turned OFF (forward-safe).
// Pure presentation: no BE endpoint, no new dependency, no money/auth/network
// path. The crop-to-bar drag marquee and the Patterns background tab remain
// DESIGNED-not-built (each its own unit) and are NOT gated by this flag.
export const TITLE_BAR_DECK15_ENABLED = truthy(
  process.env.REACT_APP_TITLE_BAR_DECK15
);

// FORM_BLOCK_SUBMIT_ENABLED — the LIVE (View Wheel) submission behaviour of the
// Form content block (bucket-D wave D2 "block-form"; deck concepts/builder-blocks/
// 05-form-block). The Form AUTHORING block + field-list editor already shipped
// (Wave-5 Lane-N) and render a STATIC preview in both the builder and the live
// wheel — a visitor sees the form but the fields are inert and Send does nothing.
// OFF (default) keeps every surface BYTE-IDENTICAL to today: the block renders the
// exact same static preview (FormBlock's FieldPreview + dead Send button) in the
// builder AND in ViewWheel, FormLiveView is never imported/mounted, and NO
// form-response network call is ever made. ON swaps ONLY the live render (craft
// editor enabled === false, i.e. ViewWheel) for an interactive, submittable form:
// real typed inputs per field, required + email validation, inline errors, a
// hidden honeypot anti-spam field, submitting/success/error states, and a POST to
// the form-response endpoint (react-query). The builder render stays the static
// authoring preview even when ON. Pairs with a SEPARATE, coordinated backend
// form-response STORAGE endpoint (the owner Responses inbox / analytics-viewers
// DL-058 is the store owner) which is premium-gated + fail-closed server-side; with
// that endpoint absent the submit simply errors into the block's error state, and
// OFF keeps the whole path inert. Anti-spam mirrors the app's forms intent (the
// existing support form uses a reCAPTCHA token; there is no honeypot in the app
// today) with a lightweight self-contained honeypot, leaving a recaptcha_token
// payload seam for the BE owner. Touches no money/auth path.
export const FORM_BLOCK_SUBMIT_ENABLED = truthy(
  process.env.REACT_APP_FORM_BLOCK_SUBMIT
);

// MEDIA_LIBRARY_PICKER_ENABLED — the reuse-in-context "Media Library" MODAL on the
// builder media picker (DL-048; deck states 03 grid / 06 list). The standalone Media
// Library page (WL-023) already ships; this flag mounts, at the shared layer inside
// Shared/MediaSelection, a "Reuse from your Media Library" affordance that opens the
// deck modal so a Segment/Wheel block REUSES media the user already uploaded instead
// of re-uploading. Mounted at the shared layer so EVERY MediaSelection consumer
// (Segment blocks, Portrait, wallpaper/segment-image, gallery details, …) inherits it
// for free (Gordon universal-access addendum 2026-07-14). OFF (default) keeps
// Shared/MediaSelection BYTE-IDENTICAL to today: neither the affordance nor the modal
// renders, no media read fires (the connected picker is `enabled: open` and is not
// even mounted while OFF). ON renders the affordance ONLY where MediaSelection already
// shows tabs (categories.length > 0 — so the tab-less DMCA proof upload, categories=[],
// is untouched); the modal reuses the Media Library's own read (getTotalMedia),
// normalizer, pure helpers and MediaGrid — no fork — filters selectable items to the
// surface's accepted media types (ineligible tabs hidden, ineligible items shown
// disabled), and inserts the chosen asset through the SAME handleChange(type, value)
// path the existing sources use. Reads already-uploaded media only; touches no
// money/auth path.
export const MEDIA_LIBRARY_PICKER_ENABLED = truthy(
  process.env.REACT_APP_MEDIA_LIBRARY_PICKER
);

// STOCK_IMAGE_LIBRARY_ENABLED — the curated "Stock images" source (LIB-image-library;
// deck concept procedural-libraries/05-image-library). The curated preset image set
// (proceduralLibrary/presets/manifest — the same one the Wallpaper "Preset images" grid
// ships) is surfaced as a first-class source inside Shared/MediaSelection so it is
// selectable as a fill / portrait / image-block / wallpaper wherever media is attached,
// not just as a wallpaper. Selecting a stock image routes it through the SHARED
// moderation wrapper (utils/mediaModeration.routeImageThroughModeration) — the SAME gate
// uploads pass through — so no image reaches a wheel surface without going through one
// governance point (stock is pre-vetted → approved without a network round-trip).
// OFF (default) keeps Shared/MediaSelection BYTE-IDENTICAL: the "Stock" tab is not added
// (effective categories === the caller's categories), nothing renders, no manifest is read.
// ON adds the "Stock" tab ONLY where MediaSelection already shows tabs AND the surface's
// media type is image. Touches no money/auth path.
export const STOCK_IMAGE_LIBRARY_ENABLED = truthy(
  process.env.REACT_APP_STOCK_IMAGE_LIBRARY
);

// BUILDER_CHROME_FIX_ENABLED — Wheel Builder chrome cleanup (LIVE-QA L3, Gordon
// 2026-07-14). Two coupled, measured chrome fixes on the /builder surface:
//  (a) Remove the redundant top-left Undo/Redo strip
//      (pages/SegmentBuilder/UndoRedo.jsx). Its ~40px height sat ABOVE the 92vh
//      builder body inside the main content area, which forced a SECOND
//      full-height vertical scrollbar there (measured: the outer scroll
//      container overflowed by exactly 40px because of the strip; hiding the
//      strip dropped that overflow to 0). Undo/Redo already lives — canonically,
//      grouped with the History trigger — in the SegmentActions action bar under
//      the wheel (Shared/UndoRedoControls), so the strip is a pure duplicate.
//  (b) Drop the builder side-pane WRAPPER's redundant vertical scroll (the
//      animated side-pane div's `overflowY:auto`). Every pane already
//      self-scrolls — Sidepane's scrollContainerRef, SidePaneWrapper's inner
//      scroll, and the shared BuilderPane `paneSurfaceCss` each set their own
//      overflow-y:auto — so the wrapper scroll only produced a 2nd, near-empty
//      scrollbar in the pane AND, by consuming ~14px of width, shifted the pane
//      content left, cutting the intended 16px side-pane left padding down to
//      ~2px ("way too little margin to left of side pane").
// OFF (default) keeps the builder BYTE-IDENTICAL to today: the strip renders and
// the wrapper keeps overflowY:auto. ON removes the strip and sets the wrapper to
// overflowY:hidden — yielding exactly ONE vertical scrollbar on the builder and
// restoring the 16px side-pane left margin (measured live on web-stg: 3
// scrollbars -> 1, left margin 2px -> 16px). Pure presentational chrome gating:
// changes NO undo/redo wiring (handlers + keyboard shortcuts untouched) and
// touches no money/auth/network path. Pairs with the staging build env line
// REACT_APP_BUILDER_CHROME_FIX=true (code stays default-OFF).
export const BUILDER_CHROME_FIX_ENABLED = truthy(
  process.env.REACT_APP_BUILDER_CHROME_FIX
);

// SETTINGS_CRUNCH_FIX — mechanical layout hardening for Settings ›
// "Subscription & Billing" (settings-crunch). OFF (default) keeps the page
// BYTE-IDENTICAL to today: the shared NewSidebar left rail keeps its current
// clamp(200px,20vw,300px) width and every plan/tier NAME keeps today's wrapping
// (the pure helpers in constants/settingsCrunchFix return "" / undefined / the
// unchanged width). ON applies two presentational-only changes: (a) narrows the
// left rail max to 260px so it stops over-consuming content width on wide
// (>1280px) screens, and (b) pins `white-space:nowrap` + ellipsis +
// `overflow-wrap:normal` on the plan/tier NAME elements — the Billing "Current
// Plan" name (Settings/Billing/styles PlanName) and the Subscription tier-card
// titles (Referrals/children/SubscriptionItem) — so a single-word name like
// "Premium" can never break mid-word ("Premiu"/"m"). The shared <TextView>
// inherits overflow-wrap:break-word, which is what lets a name split mid-word in
// a narrow box; this pins the guarantee on the name element itself. Touches no
// money/auth/network path — pure CSS gating.
export const SETTINGS_CRUNCH_FIX = truthy(
  process.env.REACT_APP_SETTINGS_CRUNCH_FIX
);

// WHEEL_SEGMENTS_FIRST_PAINT_ENABLED — draw the wheel's segment arcs on FIRST
// mount for the no-resize consumer (the Edit Wheel / builder). Bug: on the Edit
// Wheel page (/builder/:id -> WheelComponent with resize={false}) the segments
// around the portrait are INVISIBLE on first paint and only appear once a
// side-pane interaction happens. Root cause: WheelComponent's init effect builds
// the D3 CreatePieChart instance on first mount but never calls updateArcs()
// (the constructor draws only the SVG + center portrait; arcs are drawn ONLY by
// updateArcs, which lives in the effect's ELSE branch). Because the instance is
// held in a ref, creating it triggers no re-render, so the else branch never
// fires on mount; and resize={false} disables the ResizeObserver whose initial
// callback would otherwise have drawn the arcs (as it does for every resize=true
// consumer). So the builder has no mount-draw path and segments stay hidden until
// a dep change (side-pane interaction) reaches the else branch. OFF (default)
// keeps WheelComponent BYTE-IDENTICAL to today for EVERY consumer. ON draws the
// arcs immediately after construction, but ONLY when resize is false (the exact
// broken configuration) — resize=true consumers still draw via their
// ResizeObserver and are untouched (no double-draw / flicker). Purely a render
// correctness fix: the drawn result is identical to what already appears after
// the first interaction. Touches no money/auth/network path. Staging flips this
// on via REACT_APP_WHEEL_SEGMENTS_FIRST_PAINT=true so staging is never dark.
export const WHEEL_SEGMENTS_FIRST_PAINT_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_SEGMENTS_FIRST_PAINT
);

// BUILDER_EMPTY_SEGMENT_FIX_ENABLED (ISP-9999 V2) — the REAL fix for the staging-only
// "can't drag blocks into a Segment" regression.
//
// This supersedes an earlier V1 mitigation, BUILDER_CANVAS_REMOUNT_ENABLED
// (REACT_APP_BUILDER_CANVAS_REMOUNT), retired 2026-07-22: V1 keyed the craft
// <Frame> by segment id to force a remount on segment change, but it did not
// cure the regression — the empty deserialize below still wiped the freshly-
// built ROOT. V1's flag, code path, and CI env var have been removed; this
// flag now owns BOTH the empty-deserialize skip AND the <Frame> remount key.
//
// ROOT CAUSE (measured live on bundle main.3467c0c4.js via React-fiber into the craft
// store + Redux): staging persists an EMPTY segment's `nodes` as the string "{}".
// SegmentView's load guard `if (selectedArc?.nodes)` treats "{}" as content and calls
// deserialize("{}"), which craft resolves to replaceNodes({}) -> the editor store ends
// with ZERO nodes and NO ROOT. With no ROOT the <Frame> has no valid drop parent:
// hovering throws tiny-invariant ERROR_INVALID_NODEID (the observed `Invariant failed`),
// and every drop aborts in addNodeTree -> getParentAndValidate(ROOT) (silently swallowed
// by Sentry) -> no block added (0/344 staging wheels ever got content). Prod never hits
// this because prod empty segments carry null/absent nodes (guard skips -> the <Frame>
// builds its own EmptyContainerWidget ROOT), not "{}".
//
// The fix (data-agnostic): when ON, SegmentView SKIPS deserialize for an empty/rootless
// node map (isEmptyNodeMap) so the fresh empty EmptyContainerWidget ROOT the keyed <Frame>
// builds for the segment survives as a valid (empty) drop target; and it keys the <Frame>
// by segment id so switching segments rebuilds a clean ROOT (clears stale content).
// Non-empty maps deserialize exactly as today. OFF (default) keeps the builder
// BYTE-IDENTICAL to today. Builder-only (isBuilder); never touches the public
// ViewWheel/preview or any money/auth path.
//
// VERIFIED client-side on the LIVE staging bundle: patching the empty deserialize out of
// the running page took the store from {count:0, rootPresent:false} to {count:1,
// rootPresent:true, ids:["ROOT"]} with ZERO `Invariant failed` pageerrors (was >=1 every
// unpatched run). End-to-end drop still needs a post-deploy qa-validator drag pass.
// See DRAG-REGRESSION-FIX-V2.md.
export const BUILDER_EMPTY_SEGMENT_FIX_ENABLED = truthy(
  process.env.REACT_APP_BUILDER_EMPTY_SEGMENT_FIX
);

// PRIMARY_WHEEL_CLIP_FIX_ENABLED — fixes the top/bottom (and left/right) CLIP of the
// featured "Primary Wheel" card's wheel on the Gallery (web-stg/gallery/<id>), including its
// no-portrait fallback to the plain (non-"Beta") iYuga brand logo (ASSETS.IyugaWhiteLogo). ROOT
// CAUSE: PrimaryWheel/index.jsx sizes the wheel container at a fixed VIEWPORT dimension
// (containerDivStyle {width:"12vw",height:"12vw"}), but the elevated card is a FIXED 240px-tall
// box (WheelListing/index.js `<Stack sx={{height:"240px"}}>`, inner ~196px after 20px padding)
// with overflow:hidden. D3 (createPie.js) draws a SQUARE of Math.min(clientW,clientH); because
// the parent is forced to 12vw, on wide viewports 12vw (230px@1920, 307px@2560) exceeds both the
// ~196px inner height AND the ~168px 30%-grid column, so the wheel (or its logo fallback)
// overflows and the card clips its top+bottom. The small gallery-grid wheels don't clip (their
// containers are square cells sized to their own box).
//
// ISP-9999 (BUG #902, Gordon 2026-08-21): this fix was built and staging-verified behind the flag
// (forced "true" in DevOps-Sandbox.yml for that verification) but never graduated to its default,
// so production still shipped the clipped ("byte-identical to today") path — exactly the "no-Beta
// logo has its top cut off" defect QA reported. Graduating the default to ON here is the fix: the
// container gets a DEFINITE height (176px, derived from the fixed 240px card's ~196px inner box
// less ~20px margin) + width:100% + flex-center, so createPie's Math.min(colWidth, 176) draws the
// largest square that fits BOTH the column and the card height — fully contained + centered at
// every viewport width. A percentage height is NOT used: the wheel is a CSS-grid cell whose auto
// row makes % height indefinite, which re-clipped the wheel (measured live: -49.5px@1920 /
// -137.5px@2560). Deck-aligned (concepts/gallery-profile-card/04-primary-wheel-card). Single
// shared render (PrimaryWheel is the only primary/featured-wheel component); pure presentational
// CSS; touches no data/money/auth path.
export const PRIMARY_WHEEL_CLIP_FIX_ENABLED = true;

// CONSENT_UX_FIX_ENABLED — two fixes to the GDPR "We value your privacy" consent
// bottom-sheet (ConsentBanner). OFF (default) keeps the banner BYTE-IDENTICAL to
// today: the root className stays "iyuga-consent-banner" (no --fix modifier, so
// the new CSS rule never matches) and consentHelper persists to localStorage only,
// exactly as before. ON applies two flag-gated changes:
//
//   (1) WHITESPACE (mobile). At <=560px the banner __inner switches to
//       flex-direction:column, but the base `.iyuga-consent-banner__text` rule's
//       `flex: 1 1 420px` is NOT overridden there — so in the column layout the
//       420px flex-BASIS is applied as the text block's HEIGHT, forcing it to
//       ~420px tall while its content is only ~88px. MEASURED on live staging
//       (web-stg, 390px): banner 517px / text 420px = ~332px of dead vertical
//       space. The `--fix` modifier resets the text block to content height
//       (flex:0 0 auto) inside the mobile media query -> banner ~185px / text
//       ~88px (measured). Desktop/tablet (row layout) are untouched.
//
//   (2) PERSISTENCE (cross-subdomain). The choice is additionally written to a
//       domain-wide cookie (`.iyuga.net`, 180-day max-age) alongside localStorage,
//       and read cookie-first, so a single Accept/Reject suppresses the banner
//       across ALL iYuga subdomains. localStorage is per-ORIGIN, so a choice made
//       on one host (e.g. web-stg) never carried to another (app-stg / www),
//       re-showing the banner on each. Existing localStorage choices are still
//       honoured (migration). Loads NO analytics (GA still only inits after
//       explicit consent === "granted"); touches no money/auth path.
export const CONSENT_UX_FIX_ENABLED = truthy(
  process.env.REACT_APP_CONSENT_UX_FIX
);

// ONBOARDING_TOUR_FIX_ENABLED — fixes the first-run Gallery Joyride tour
// (EW-1020 🔴 + EW-1021 🟠; NEMESIS-nemz11-onboarding). ROOT CAUSE (EW-1020):
// pages/Gallery/index.jsx runs react-joyride in UNCONTROLLED continuous mode
// (no `stepIndex`); on the very first hop from the `target:"body"` centered
// Welcome step (idx0) to the `.create-wheel-button` element step (idx1),
// react-joyride's own target found/visible check races the spotlight+scroll
// init, reads idx1's target as "not yet visible" and AUTO-ADVANCES one extra
// step (its TARGET_NOT_FOUND path, guarded by `!controlled`) — so the Welcome
// primary jumps idx0->idx2 and idx1 "Create Wheel" (the core coach-mark) is
// skipped every time and is structurally unreachable via the tour's own CTA.
// EW-1021: that primary button reads "Explore Now", implying it navigates to
// /explore, but it only advances the tour.
// OFF (default) keeps the tour BYTE-IDENTICAL to today: <Joyride> is passed
// `stepIndex={undefined}` (uncontrolled — react-joyride ignores a non-number
// stepIndex, exactly the current behavior), the controlled-advance callback
// branch and the start-reset effect are no-ops, and the Welcome button reads
// "Explore Now". ON runs the tour CONTROLLED — the app owns `stepIndex` and
// advances it deterministically (idx0->idx1->idx2…, exactly one step per user
// action) in handleJoyrideCallback, so react-joyride never auto-skips a step and
// the Welcome primary lands on idx1 "Create Wheel" — and relabels that button
// "Take the Tour" so the copy matches what it does (start the walkthrough). The
// advance math lives in the pure, unit-tested GalleryTooltip/tourNav.js. Touches
// no money/auth/network path. Pairs with the staging build env line
// REACT_APP_ONBOARDING_TOUR_FIX=true (code stays default-OFF).
export const ONBOARDING_TOUR_FIX_ENABLED = truthy(
  process.env.REACT_APP_ONBOARDING_TOUR_FIX
);

// TOUR_RESUME_ENABLED — EW-1022 🟡 (NEMESIS-nemz11-onboarding). The first-run
// Gallery Joyride tour restarts from the Welcome step (idx0) on ANY mid-tour
// remount — an F5 refresh, or browser Back-then-Forward — because `runTour`
// recomputes to true from localStorage+profile and the controlled-index mount
// effect forced `stepIndex` back to 0. So an accidental refresh at step 7 of 8
// costs the user the whole tour again. When ON, the controlled stepIndex is
// PERSISTED to localStorage on every advance and RESTORED on mount, so the tour
// resumes where the user left off; it is cleared on finish/skip. OFF (default)
// keeps today's behavior BYTE-IDENTICAL: nothing is written to or read from
// storage and the mount effect still forces idx0. Resume is only meaningful when
// ONBOARDING_TOUR_FIX_ENABLED is also ON (the tour must be CONTROLLED for a
// restored index to take effect) — an uncontrolled <Joyride> ignores stepIndex.
// Touches no money/auth/network path. Pairs with the staging build env line
// REACT_APP_TOUR_RESUME=true (code stays default-OFF for prod).
export const TOUR_RESUME_ENABLED = truthy(process.env.REACT_APP_TOUR_RESUME);

// WHEEL_OPEN_REDUCED_MOTION_ENABLED — respect prefers-reduced-motion in the public
// ViewWheel open-choreography (EW-1240 / WHEEL-COOL-FACTOR-UPLIFT.md §1.5). When a wedge
// is opened the page grid reshapes (desktop: 25%/50%/25% -> 0%/70%/30%; mobile-landscape:
// 0%/100%/0% -> 0%/50%/50%) over a 0.5s `grid-template-columns` transition (ViewWheel
// styles.js Grid), sliding the wheel ~200px sideways to reveal the Segment panel — a large
// translational motion that TODAY ignores prefers-reduced-motion (measured byte-identical
// trajectory under reducedMotion:reduce), unlike the app's ~38 other reduced-motion-aware
// surfaces. OFF (default) keeps the page BYTE-IDENTICAL to today: the Grid (and the
// CenterColumn transform tween) always animate the open slide for every user. ON gates
// ONLY the transition duration on the OS setting via the shared useReducedMotion hook — a
// reduced-motion user gets an INSTANT layout snap (transition: none) on open while
// full-motion users keep the 0.5s slide. Pure presentational a11y gating: the final
// laid-out position is identical either way (only the tween is removed); touches no
// data/money/auth/network path.
export const WHEEL_OPEN_REDUCED_MOTION_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_OPEN_REDUCED_MOTION
);

// GRAPH_LABEL_CAP_ENABLED — EW-1063 guard for the Graph content block. The block's
// per-datapoint LABEL was uncapped: GraphSettings stored the raw <input> string and
// GraphChart drew it as an SVG <text> (no wrap/truncate) inside the fixed 360x238
// viewBox, so a 60+ char label overflowed the chart / collided with neighbours (the
// sibling TITLE input was already capped to 60; labels were not). OFF (default) keeps
// the block BYTE-IDENTICAL to today: the label <input> carries no maxLength and its
// onChange stores the raw value, and every axis/legend label renders the raw string.
// ON applies two additive layers — (1) a maxLength + slice hard cap on the label input,
// and (2) an ellipsis truncation (graphMath.truncateLabel) on every rendered axis/legend
// label with a context char-budget (adapts to point count / legend cell width) — so no
// label can overflow the chart regardless of how it was entered or previously saved.
// Pure presentational + input bound; touches no data/money/auth path.
export const GRAPH_LABEL_CAP_ENABLED = truthy(
  process.env.REACT_APP_GRAPH_LABEL_CAP
);

// SEARCH_KEYBOARD_NAV_ENABLED — keyboard operability for the global "Search
// iYuga" top-nav results dropdown (EW-1182 / NEMESIS-nemz19-search). Today the
// dropdown is keyboard-inert: a user can type a query and see results, but
// ArrowUp/Down move nothing and Enter does nothing — there is NO non-mouse way
// to open a result (WCAG 2.1.1 gap). OFF (default) keeps the SearchBar
// BYTE-IDENTICAL to today: no onKeyDown is bound, no ARIA combobox/listbox/option
// roles or ids are added, no highlight state renders, and each result row is the
// exact same markup as now (no data attribute, no role, no highlight style). ON
// wires ArrowDown/ArrowUp to move a wrapping highlight through the ACTIVE tab's
// visible results (Users or Wheels), Enter to OPEN the highlighted row (or the
// first row when none is highlighted) by dispatching a native click on that SAME
// row the mouse uses (searchKeyboardNav.js decides the index; the component just
// clicks the node) — so the mouse open path is reused verbatim, not forked — and
// Escape to close the dropdown. It also adds the standard combobox ARIA wiring
// (role=combobox + aria-expanded/controls/activedescendant on the input,
// role=listbox on the results container, role=option + aria-selected + a visible
// brand highlight on each row). Pure client-side UX/a11y; touches no
// data/money/auth/network path. Pairs with the staging build env line
// REACT_APP_SEARCH_KEYBOARD_NAV=true (code stays default-OFF).
export const SEARCH_KEYBOARD_NAV_ENABLED = truthy(
  process.env.REACT_APP_SEARCH_KEYBOARD_NAV
);

// REFERRAL_INVITE_FIX_ENABLED — fixes the referral "Share iYuga" modal
// (NEMESIS-nemz16 / EW-1122 + EW-1121). Two defects, one flag:
//   • EW-1122 (copy): the "Invite Message with Link" Copy button put a DIFFERENT
//     string on the clipboard than the message shown on screen — different name
//     form, different marketing copy, and a raw unresolved "!..." mid-string.
//     ON derives the rendered message AND the clipboard message from ONE source
//     (ShareInviteModal/inviteMessage.js) so they cannot drift; the clipboard is
//     byte-for-byte the approved on-screen message.
//   • EW-1121 (a11y): the only entry to the flow — the gallery/wheel "Invite"
//     control (EnterBetaButton) — was an alt-less <img> in tabIndex=-1 divs with
//     no role/aria, so it was 0% keyboard/screen-reader reachable; the modals
//     also carried no dialog semantics. ON makes EnterBetaButton a real focusable
//     control (role=button + tabIndex=0 + aria-label + Enter/Space activation,
//     alt="" on the decorative images) and gives the Share/Invite modals
//     role="dialog" + aria-modal + aria-label + Escape-to-close + focus trap.
// OFF (default) keeps BOTH the copy path and every EnterBetaButton usage (gallery
// header, wheel header, navbar, mobile search header) BYTE-IDENTICAL to today.
// Pure client-side UX/a11y; touches no data/money/auth/network path. Pairs with
// the staging build env line REACT_APP_REFERRAL_INVITE_FIX=true.
export const REFERRAL_INVITE_FIX_ENABLED = truthy(
  process.env.REACT_APP_REFERRAL_INVITE_FIX
);

// REFERRAL_PROGRAM_ENABLED / REFERRAL_PROGRAM_SUSPENDED — the single switch for
// every user-facing mention of the Referral / "Earnings Program" (Gordon,
// 2026-07-30: "Hide Referral mentions").
//
// The programme is SUSPENDED, not deleted: the app was still selling it (the
// tier-card blurb monetized it, the Invite modal pitched an eligibility gate the
// viewer already passed, the Gallery tooltip advertised an "Earnings Portal"),
// i.e. in-app marketing for something that does not run.
//
// This flag HIDES that marketing — it removes nothing. Every dashboard, table,
// hook, redux slice, route and API call stays on disk exactly as it is; the
// referral-code plumbing (`referralData`, `/signup/:referralCode`, attribution)
// is deliberately left INTACT so nothing is lost while the programme is paused.
// When the programme resumes, set the staging/prod build env line
// REACT_APP_REFERRAL_PROGRAM=true and every surface comes back together.
//
// This is the ONE shared primitive for the whole set (G-118) — it replaces the
// file-local `EARNINGS_PROGRAM_SUSPENDED` const that used to live inside
// components/ProfileMenu/index.jsx and only governed that one dropdown entry.
// Do NOT re-implement a second referral switch anywhere else; import this.
//
// It does NOT govern creator Paid-Access earnings (LockSettingsModal /
// Analytics > Monetization) — that is a different feature that is not suspended.
export const REFERRAL_PROGRAM_ENABLED = truthy(
  process.env.REACT_APP_REFERRAL_PROGRAM
);
export const REFERRAL_PROGRAM_SUSPENDED = !REFERRAL_PROGRAM_ENABLED;

// HTTPS_SHARE_URL_ENABLED — force the Wheel Share modal onto https (NEMESIS
// nemz20 / EW-1201). The Share Wheel modal builds the copy-link value AND all 6
// social share targets (Facebook, Pinterest, LinkedIn, X, Messenger, WhatsApp)
// from one source — `wheel.share_url` — which the backend returns with an
// `http://` scheme. So the FIRST hop of the copy-link and every share window,
// including the `?s=<token>` share token, travels in CLEARTEXT before the ELB
// upgrades it. OFF (default) keeps the modal BYTE-IDENTICAL to today: currentUrl
// is `wheel.share_url` verbatim (http:// as the backend sends it). ON routes that
// single value through toHttpsShareUrl(), upgrading a leading http:// to https://
// (localhost/127.0.0.1 dev hosts left untouched — no TLS there), so no share
// surface ever emits the token over an unencrypted first request. Pure
// presentational/URL hardening — no new network call, touches no money/auth path.
// Pairs with the staging build env line REACT_APP_HTTPS_SHARE_URL=true (code
// stays default-OFF). The lasting cleartext-token fix is also backend-side (make
// the BE emit https share_url) — tracked as an EW-1201 BE follow-up.
export const HTTPS_SHARE_URL_ENABLED = truthy(
  process.env.REACT_APP_HTTPS_SHARE_URL
);

// SEARCH_INPUT_HARDENING_ENABLED — hardens the global "Search iYuga" top-nav
// search box (EW-1181 / NEMESIS-nemz19-search). Today an over-long query (the
// nemesis pasted 10,000 chars) is sent verbatim as
// GET /api/v2/search-users/?search=<10k>… and dies at the CDN/LB URL-length
// limit (net::ERR_FAILED); the failure is swallowed — the dropdown keeps stale
// rows from the previous query with NO user feedback (Explore search already
// defends against this per EW-604; global search did not). OFF (default) keeps
// the SearchBar BYTE-IDENTICAL to today: no maxLength on the input, the fetch
// catch block only console.error's, and the empty-state renders exactly as now.
// ON (1) caps the input at SEARCH_MAX_QUERY_LEN (200) chars via a maxLength
// attribute AND a clampSearchQuery on change — so the oversized request can
// never be dispatched — and (2) on ANY search-request failure clears the stale
// rows and shows a "Search failed … try again" message that takes precedence
// over the "No … Found" empty state. Pure client-side UX/resilience; touches no
// data/money/auth path and changes no request shape (only its max length).
// Pairs with the staging build env line REACT_APP_SEARCH_INPUT_HARDENING=true.
export const SEARCH_INPUT_HARDENING_ENABLED = truthy(
  process.env.REACT_APP_SEARCH_INPUT_HARDENING
);

// NOTIF_CENTER_ENABLED — the reachable notification-center UI (bell entry point
// in the top nav + a popover panel that lists the notification feed). Closes
// EW-1142 (NEMESIS-nemz17): the notification LIST backend is fully live +
// populated (51 real items) and is already silently polled on every page load,
// but the bell/dropdown in GalleryHeader was commented out so NOTHING renders it
// — a permanent invisible unread pile. OFF (default) keeps the header
// BYTE-IDENTICAL to today: the <NotificationCenter/> is not rendered, no bell
// appears, and the react-query notifications query is disabled (never fires), so
// the existing legacy redux poll remains the only fetch exactly as now. ON
// renders the flag-gated bell + Badge unread count + accessible MUI Popover panel
// (loading / empty / error / populated states) driven by react-query
// (useNotifications), reuses the existing NotificationItem + EmptyState design
// components, offers "Mark all as read" (markAllRead mutation + invalidate) and
// "View all" -> the existing /notifications route, and — because it owns the
// fetch — the redundant legacy GalleryHeader poll is skipped when ON (removes the
// EW-1142 wasted round-trip). SECURITY (EW-1143): notification `description` is
// raw server HTML (actor name inside <b> tags); it is rendered via
// renderSafeDescription (React text nodes only, NEVER innerHTML /
// dangerouslySetInnerHTML) so an injected <img onerror=…> display name is inert
// text, not an execution vector. Pairs with the staging build env line
// REACT_APP_NOTIF_CENTER=true. Touches no money/auth path.
export const NOTIF_CENTER_ENABLED = truthy(
  process.env.REACT_APP_NOTIF_CENTER
);

// AI_WHEEL_GENERATOR_ROUTE_ENABLED — surfaces the already-built AI Wheel
// Generator (NEMESIS AI-03: full generator ships in the bundle — NewAIBuilder
// panel + redux `aiWheels` slice + GENERATE_AI_WHEEL /wheel-generate-premium/ —
// but has NO route and NO nav entry, so the paid feature is dark/undiscoverable).
// OFF (default) keeps the app byte-identical to today: the /create/ai-wheel route
// is not registered (falls through to the 404 catch-all) and the Navbar "Create
// with AI" dropdown entry is not rendered, so the surface is unreachable. ON
// registers the route + the flag-gated Navbar entry that opens the existing
// NewAIBuilder generator. The generator POSTs to the BILLABLE OpenAI generate
// endpoints, so FLIP-ON IS GATED on BE AI-01 throttle/tier gating landing +
// AI-05 server-side per-tier token/rate enforcement being verified (a Free user
// must not be able to drive paid OpenAI spend). Client tier/token gates here are
// UX only; the server is the backstop. Prod go-live = Gordon decision.
export const AI_WHEEL_GENERATOR_ROUTE_ENABLED = truthy(
  process.env.REACT_APP_AI_WHEEL_GENERATOR_ROUTE_ENABLED
);

// SEGMENT_ENVIRONMENTS_ENABLED — per-segment "Segment Environment": each segment
// may OPT IN to its OWN Wheel Portrait (#1, the CENTER image) AND its OWN Wheel
// Wallpaper (#2, the whole-PAGE background) that replace the wheel's defaults ONLY
// when that segment is selected on the View Wheel (SEGMENT-ENVIRONMENTS-SPEC.md,
// Gordon 2026-07-16). It is strictly a per-segment override of the two WHEEL-level
// visuals — the segment's OWN fill (#3), content wallpaper (#4) and frame color (#5)
// are UNTOUCHED. OFF (default) keeps every surface BYTE-IDENTICAL to today: the
// builder's Segment Settings renders NO "Segment Environment" section, no env data is
// written, and ViewWheel never reads a segment's env fields — every segment shows the
// wheel's own default Portrait + Wallpaper exactly as now. ON adds (a) the builder
// assign panel (reusing the existing Wheel Portrait + Wheel Wallpaper pickers; uploads
// route through the moderation wrapper handleFileUpload) that writes the segment's
// nullable env_center_media / env_wallpaper_settings, and (b) the ViewWheel swap: on
// segment select, if THIS segment has an assigned env portrait it cross-fades the
// CENTER to it (else keeps the wheel's default portrait / iYuga logo — never blank,
// never a synthetic glyph, honoring the §12 center law), and if it has an assigned env
// wallpaper it cross-fades the PAGE background to it (else keeps the wheel default). A
// segment with no env behaves exactly as today. Crossfade is GPU-only opacity
// (~380ms ease) and honors prefers-reduced-motion (instant swap). The BE stores the
// two nullable Activity fields as pure passthrough (inert when NULL); this FE flag
// governs both the authoring UI and the view swap. Pairs with the staging build env
// line REACT_APP_SEGMENT_ENVIRONMENTS=true. Touches no money/auth/network path.
export const SEGMENT_ENVIRONMENTS_ENABLED = truthy(
  process.env.REACT_APP_SEGMENT_ENVIRONMENTS
);

// COMMAND_PALETTE_ENABLED — global Ctrl/⌘+K command palette (CommandPaletteRoot,
// mounted once in App.js). OFF (default) keeps the app byte-identical to today:
// CommandPaletteRoot is NOT mounted, no global keydown listener is attached, and
// the launcher pill is not rendered. ON mounts the palette overlay + hotkey +
// quiet launcher for logged-in users. Additive navigation aid; touches no
// money/auth path. Governance flag added 2026-07-16 (govern-live-delights): the
// palette shipped to sandbox UNFLAGGED and silently live — this makes it
// governed + reversible without a code revert. Staging build env pins
// REACT_APP_COMMAND_PALETTE=true so staging stays live.
export const COMMAND_PALETTE_ENABLED = truthy(
  process.env.REACT_APP_COMMAND_PALETTE
);

// WHEEL_KEYBOARD_NAV_ENABLED — arrow-key / Enter keyboard navigation of the
// public View Wheel as a composite widget (D3 engine createPie `keyboardNav`
// config on ViewWheel). OFF (default) restores the engine's built-in default
// (keyboardNav=false): no focus ring, no arrow/Enter handlers bound — the wheel
// is byte-identical to a non-opt-in WheelComponent. ON makes the public
// ViewWheel keyboard-navigable (arrow through segments, Enter opens — the same
// handler as a click). Scoped to the public View Wheel only; other
// WheelComponent usages are unaffected. This is NOT the SearchBar's
// REACT_APP_SEARCH_KEYBOARD_NAV (a different surface). Governance flag added
// 2026-07-16 (govern-live-delights): the a11y nav shipped hardcoded ON — this
// makes it governed + reversible. Staging build env pins
// REACT_APP_WHEEL_KEYBOARD_NAV=true so staging stays live. Touches no
// money/auth path.
export const WHEEL_KEYBOARD_NAV_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_KEYBOARD_NAV
);

// FIRST_VIEW_WIZARD — a skippable, shown-once, NON-BLOCKING coach-mark overlay for
// a first-time WHEEL viewer/guest on the public View Wheel page (wave6;
// FIRST-VIEW-WIZARD-spec.md; audit FIRST-VIEW-GUEST-AUDIT.md:111 — a touch guest
// gets zero signal the ring is tappable). OFF (default) keeps the View Wheel page
// BYTE-IDENTICAL to today: the overlay chunk is lazily imported ONLY behind this
// flag (never fetched while OFF), no localStorage/profile-field read happens, and
// the additive `data-intro-target` anchor attributes are NOT emitted on the
// slice/heart/clone/creator controls. ON mounts, once per first-time viewer, a
// 2–4 beat spotlight sequence over the already-rendered Wheel — the cutout keeps
// the target fully tappable (Skip / × / backdrop / tapping the target all dismiss),
// PLAIN-language copy (no Wheel/Segment/Collect/Clone/Gallery/Follow), reduced-
// motion + WCAG AA. Guests persist "seen" in localStorage (`iyuga_viewer_intro_seen`,
// zero backend dependency); logged-in viewers additionally use the additive nullable
// `first_view_intro_completed_at` profile field (separate BE draft-PR contract,
// spec §5 — mirrors show_walkthrough). Touches no money/auth path.
export const FIRST_VIEW_WIZARD = truthy(
  process.env.REACT_APP_FIRST_VIEW_WIZARD
);

// RESPONSIVE_GET_APP_BUTTON (QA-148 #3, Gordon 2026-08-18) — a PERMANENT,
// always-visible "Get the app" button on the responsive (phone-browser) View
// Wheel that re-opens the SAME landing app-store download popup
// (components/MobileView/Popup) so a guest can enter the app-download +
// referral-email flow at any time, not just the one-shot popup on first paint.
// OFF (default) keeps the responsive View Wheel BYTE-IDENTICAL to today: the
// permanent button is never mounted, so prod/other envs are unaffected until the
// flag is turned on (staging-flags-always-on sets it "true" in DevOps-Sandbox).
export const RESPONSIVE_GET_APP_BUTTON = truthy(
  process.env.REACT_APP_RESPONSIVE_GET_APP_BUTTON
);

// WALLPAPER_SVG_LIBRARY_ENABLED — stock the "Preset wallpapers" SVG library in
// the Wheel/Segment Wallpaper picker (deck concept 01-wallpaper-svg-library:
// "Preset wallpapers — every tile is generated art, no upload needed"). Today
// the Wallpaper picker's Image > Preset sub-tab shows only raster/URL preset
// wallpapers (wallpapersReducer.wallpapers), and the full-scene generated-art
// wallpaper library the deck implies is MISSING (the tileable `patterns` are a
// separate concept). OFF (default) keeps the picker BYTE-IDENTICAL to today: the
// Preset grid renders only the existing raster presets, no wallpaperLibrary code
// runs, and nothing new is read or written. ON prepends a "Preset wallpapers"
// section (40 generated full-scene SVG tiles — layered peaks / wave bands /
// concentric rings / curve split / triangle peaks across 8 palettes, palette #1
// = brand violet #9B5AF8) to the Preset grid; selecting a tile writes its
// self-describing `data:image/svg+xml,...` URL to the SAME `bgWallpaper` image
// slot (currentBg === 2) the raster presets already use — no new redux field, no
// contract change, no network, no raster (GPU-composited background images). A
// wallpaper saved while ON still renders if the flag is later turned OFF
// (forward-safe: it is just a stored image src). Touches no money/auth/network
// path — pure additive presentational gating.
export const WALLPAPER_SVG_LIBRARY_ENABLED = truthy(
  process.env.REACT_APP_WALLPAPER_SVG_LIBRARY
);

// ICON_LIBRARY_V2_ENABLED — stocks the Segment icon picker
// (SegmentSettings/children/SegmentIcon.jsx) with the on-brand LINE-ART icon
// library from deck concept 04-icon-library (Decision #35) and the production
// sourcing decision in design-handoff/ICON-SOURCES.md (Tabler Icons, MIT,
// 24x24 / 2px stroke line-art — never emoji/raster). OFF (default) keeps the
// picker byte-identical to today: it renders the legacy `faCategories` map
// (Regular / Brands / Solid) and the new `iconLibrary` module is never read.
// ON swaps the picker's category source to the stocked line-art set — 13
// categories (Essentials, Arrows, Business, Nature, Animals, Food, Travel,
// Health, Tech, Social, Weather, Objects, Symbols) — via the SAME
// onIconSelect(<Icon />) path (no new data field, no backend contract). Rides on
// top of REACT_APP_SEGMENT_ADAPTIVE_ICONS_ENABLED (the picker surface itself);
// this flag only governs which icon set the picker offers. Touches no
// money/auth path.
export const ICON_LIBRARY_V2_ENABLED = truthy(
  process.env.REACT_APP_ICON_LIBRARY_V2
);

// WHEEL_COPILOT_ENABLED — AI Wheel Copilot side panel on the Edit Wheel page
// (decision D13). OFF (default) keeps the builder byte-identical to today: no
// Copilot rail item, no pane, and /api/v2/wheel/copilot/ is never called. ON
// adds a "Copilot" side-rail entry opening a BuilderPane-chrome panel that
// gives advice about the open Wheel and PROPOSES whitelisted edits (rename /
// recolor / add / remove / reorder Segments, copy tweaks) as cards that apply
// ONLY on an explicit Approve click — every apply goes through the builder
// history (withHistory) so it is undoable. Entitlement is SERVER-authoritative
// (Essential+, AI Wheel Token metering on the backend WHEEL_COPILOT_ENABLED
// flag); the FE tier-lock/token-empty states are cosmetic mirrors of the
// server's error codes. Pairs with backend WHEEL_COPILOT_ENABLED.
export const WHEEL_COPILOT_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_COPILOT
);

// MODERATION_CONSOLE_ENABLED — MOD-1 staff Moderation Console (/moderation): a
// staff-only review queue over the ContentFlag records the moderation pipeline
// (Phase A) fills on every quarantine. OFF (default) keeps the app byte-identical
// to today: the route is NOT registered (chunk never fetched) and the nav entry
// is not rendered. ON mounts the console; the surface is still server-authoritative
// staff-only — the BE 404s the whole API while its own MODERATION_CONSOLE_ENABLED
// is OFF and 403s any non-staff caller, so a non-staff user who reaches the route
// sees the "restricted" state, not any queue data. Base OFF; staging sets "true".
export const MODERATION_CONSOLE_ENABLED = truthy(
  process.env.REACT_APP_MODERATION_CONSOLE
);

// OPS_CONSOLE_ENABLED — OPS-1 staff Ops Console (/ops): the staff-facing counterpart
// to the Moderation Console. First surface is a read-only, paginated users/accounts
// list (staff view of the user data an admin needs). OFF (default) keeps the app
// byte-identical to today: the route is NOT registered (chunk never fetched) and no
// Ops API is called. ON mounts the console; the surface stays server-authoritative
// staff-only — the client `is_staff` check (pages/OpsConsole/staffGate.js) is a UX
// fast-path, while the BE endpoint (GET /api/v2/ops/users/, see
// OPS-CONSOLE-BE-ENDPOINT-SPEC.md) MUST require staff permission and 403 any
// non-staff caller, so a non-staff user who reaches the route sees the
// "Staff access only" state, not any user data. Base OFF; staging sets "true"
// only AFTER the staff-gated BE endpoint ships. Touches no money/auth-write path.
export const OPS_CONSOLE_ENABLED = truthy(
  process.env.REACT_APP_OPS_CONSOLE
);

// SEG_PALETTE_AUTODERIVE — auto-derive a Segment's color palette from its
// environment/fill wallpaper media (decision D18). OFF (default) keeps the
// WallpaperTab BYTE-IDENTICAL to today: selecting a wallpaper only sets the
// background; the palette is never touched and generateColorsFromImage is never
// called from this path. ON, when a Segment's wallpaper image/GIF changes, the
// EXISTING image->palette generator (services/api generateColorsFromImage) is
// invoked (debounced), the derived palette is committed to the Segment exactly
// like a manual ColorPalette "Select" (savePalette -> updateSegmentColorPalette),
// a pushToHistoryAction undo entry is pushed BEFORE the change as a safety net,
// and a "Palette updated from image — Undo" toast is shown. GIFs derive from the
// first frame (Pillow opens frame 0 by default server-side). No manual-lock UI is
// introduced (D18 chose auto-without-toggle); if a per-segment auto-derive lock
// field later exists it is respected. Touches no money/auth path.
export const SEG_PALETTE_AUTODERIVE = truthy(
  process.env.REACT_APP_SEG_PALETTE_AUTODERIVE
);

// LOGIN_WHEEL_ANIM_ENABLED (GR-7) — the Login page hero: brand Wheels appear and
// FORM the iYuga logo lockup, replacing the static auth RightView image. OFF
// (default) keeps the auth pages BYTE-IDENTICAL to today: AuthWrapper's RightView
// renders the existing static <IyugaImage src={signup_rightimg}> exactly as now,
// and the LoginWheelAnim component is imported but never rendered. ON, and ONLY on
// the Login page (scoped via AuthWrapper's `heroAnimation` prop — Signup/Forgot
// keep the static image), the RightView swaps to <LoginWheelAnim>: a curated,
// deterministic scatter of on-brand Wheels (canonical brandWheelInner — G-19)
// flies in, converges, and settles into the real iYuga logo. Purely decorative +
// GPU-only CSS motion in a separate grid column from the form — it can NEVER
// block or delay login; the form is interactive immediately. prefers-reduced-
// motion shows the still, settled logo lockup with no motion. Touches no money/
// auth/network path.
export const LOGIN_WHEEL_ANIM_ENABLED = truthy(
  process.env.REACT_APP_LOGIN_WHEEL_ANIM
);

// WHEEL_HEADER_MAPS (ISP-9999) — on the public ViewWheel, present Clone-Lineage,
// Linked-Wheels, and Translate as HEADER TOOLBAR BUTTONS (near Edit/Share) that
// open a popup, instead of the always-on permanent LeftColumn sections.
//   • OFF (default): BYTE-IDENTICAL to today — GuestTranslateButton, CloneLineageMount
//     and ConnectNetworkSection render inline in the LeftColumn exactly as now.
//   • ON: those three inline sections are suppressed; the header gains
//     - a "Translate" button (respects GUEST_TRANSLATE_ENABLED),
//     - a "Clone lineage" button, grayed unless the wheel is a clone
//       (is_cloned || is_copied_from_clone), opening a popup with the baseline
//       <LineageView> (fed by the already-live /wheel/<id>/lineage/ payload —
//       needs CLONE_LINEAGE_ENABLED only; NOT the BE CLONE_LINEAGE_POPUP_ENABLED
//       author payload, so it never reintroduces the 2026-07-16 half-deploy regression),
//     - a "Linked wheels" button, grayed unless a read-only useWheelGraph probe finds
//       the wheel HAS linked wheels (the probe never opens the upgrade modal),
//       opening a popup with the LinkedWheelsMap + drill-down.
// Independent of the Issue-1 fix in DrillDownSection (which never auto-opens the
// upgrade modal on passive view regardless of this flag). Touches no money/auth path.
export const WHEEL_HEADER_MAPS = truthy(
  process.env.REACT_APP_WHEEL_HEADER_MAPS
);

// WHEEL_MAPS_ENTRY_ENABLED (ISP-9999) — the master RE-ENABLE switch for the two
// map ENTRY POINTS on the public ViewWheel: the "Clone lineage" (Clone Map) and
// "Linked wheels" (Linked Wheel Map) header buttons + their popups + the LeftColumn
// versions (CloneLineageMount / ConnectNetworkSection). Gordon 2026-08-18: HIDE both
// for now (parked, not removed) — users should not see or reach the tree/branching/
// node-graph visualizations. Default OFF (hidden) is the whole point of this flag, so
// keep it OFF (do NOT add REACT_APP_WHEEL_MAPS_ENTRY to any staging/prod deploy env
// while the feature is parked — that is what keeps the icons hidden on web-stg even
// though staging otherwise flips flags on). The underlying CloneLineage / LinkedWheels
// components, routes, popups and their OWN flags (CLONE_LINEAGE_ENABLED /
// LINKED_WHEELS_ENABLED / WHEEL_HEADER_MAPS) are LEFT INTACT — flip this flag back ON
// (REACT_APP_WHEEL_MAPS_ENTRY=true) to restore every entry point exactly as before.
// Translate (the third WHEEL_HEADER_MAPS button) is NOT gated by this flag and is
// unaffected. Touches no money/auth/network path — pure presentational gating.
export const WHEEL_MAPS_ENTRY_ENABLED = truthy(
  process.env.REACT_APP_WHEEL_MAPS_ENTRY
);

// RV2_GIF_INTERACTION_ENABLED — GIF wedge fills ANIMATE on hover/select in the
// renderer-v2 engine (Gordon verbatim: "GIFs animate when the Segment is
// selected or when the Mouse is hovering over them"). The rv2 wedge paints a
// gif fill through an SVG <pattern><image>, which freezes it to a still poster
// (EW-009) — so today the flipped /wheel/<id> View shows GIF segments STATIC,
// always. OFF (default) keeps every rv2 surface byte-identical to today: no
// hover listeners are attached, no overlay DOM is ever created, and the frozen
// poster renders exactly as now. ON overlays — for the ONE hovered or selected
// gif wedge only, never all at once (perf: one big wheel, N gif segments ⇒ at
// most 2 playing) — a wedge-arc-clipped <foreignObject> holding an animating
// HTML <img> of the SAME gif, placed with the SAME cover math as the frozen
// pattern so poster and playing frame are pixel-aligned (the EW-003 card
// gif-hover technique, createPie.mountGifHoverWedges, ported to rv2).
// Un-hover / de-select removes the overlay → back to the still poster.
// prefers-reduced-motion: the overlay never mounts — the poster stays still
// (reduced-motion means no auto-animation; parity with the card tiles).
// Renderer-only: touches no data, save, money or auth path.
export const RV2_GIF_INTERACTION_ENABLED = truthy(
  process.env.REACT_APP_RV2_GIF_INTERACTION
);

// RV2_SELECTION_TINT_ENABLED — the renderer-v2 SELECTED-segment indicator is
// derived from the SEGMENT'S OWN COLOR instead of the fixed navy ring + purple
// glow (Gordon verbatim: "the blue outline is too extreme/harsh/doesn't fit
// the wheel style sometimes. Can the effect have something to do with the
// Segment Color?"). OFF (default) keeps the rv2 emphasis byte-identical to
// today: theme.emphasisRing || #123051 ring, the shared #9B5AF8 glow filter,
// dimming unchanged. ON derives the ring + glow for each selected wedge from
// that wedge's fill via the SHARED deck pill-colour law (pillColors.colorsFor —
// the hue itself, darkened ~34% toward black only when too light to read as a
// keyline), so a white/near-white segment's selection stays visible (the same
// contrast guard the rim pills use) and the indicator always harmonises with
// the segment. Selection stays POSITIONALLY STATIC (row 10) — ring + glow +
// dim only, never geometry. The default styling is the "hue ring" option;
// the engine keeps a selectionStyle seam ("hue-ring" | "lift" | "keyline")
// so the design-lane alternates stay testable. Renderer-only: touches no
// data, save, money or auth path.
export const RV2_SELECTION_TINT_ENABLED = truthy(
  process.env.REACT_APP_RV2_SELECTION_TINT
);

// BUILDER_LAYOUT_FIT_ENABLED — Wheel Builder canvas layout fix (Gordon
// 2026-07-24, from his /builder/2636 screenshot). Three parts, ONE container
// change:
//   (a) the wheel-wide RECOVERY family lives together — Version History moves
//       out of the under-wheel EDIT row and joins Undo/Redo in one cluster
//       ("that is not an 'edit' function of the wheel it is a wheel-wide
//       recovery"). MEASURED on web-stg 2026-07-24: History sat at (507.9, 984.9)
//       under the wheel while Undo/Redo sat at (1708.8, 98.4).
//   (b) the wheel is VERTICALLY CENTERED in the canvas pane at every width. The
//       bottom actions row is balanced by an equal top spacer, so "centered" is
//       exact rather than "centered minus the toolbar".
//   (c) the wheel FILLS the pane and RESIZES DYNAMICALLY from the pane's OWN
//       measured box (ResizeObserver — never window dimensions), so it shrinks
//       when the side pane opens and RE-ENLARGES when it closes. MEASURED before:
//       a fixed 500x500 svg top-aligned in a 500x842.4 box (327px of dead space
//       above the "+ Segment" row) and only 136.5px wide at a 390px viewport.
// It also fixes a pre-existing BUILDER_CHROME_FIX defect MEASURED the same day:
// with the side pane CLOSED the relocated Undo/Redo cluster is CLIPPED by the
// 0vw rail (hit-test at its own center misses it — elementFromPoint returns a
// different node), i.e. undo/redo is unreachable in that state. The recovery
// cluster is therefore anchored to the always-visible canvas pane instead.
// OFF (default) keeps the builder BYTE-IDENTICAL: no ResizeObserver, no new
// wrappers, History stays in the SegmentActions row, Undo/Redo stays wherever
// BUILDER_CHROME_FIX puts it. Pairs with the staging build env line
// REACT_APP_BUILDER_LAYOUT_FIT=true (code stays default-OFF).
export const BUILDER_LAYOUT_FIT_ENABLED = truthy(
  process.env.REACT_APP_BUILDER_LAYOUT_FIT
);

// BUILDER_SEGMENT_THIRDS_ENABLED — balanced "thirds" distribution of the Wheel +
// the open Segment box across the builder canvas (Gordon 2026-08-18). Problem,
// MEASURED HEADED on web-stg /builder/4802 @1440: with a Segment open the wheel
// column is `flex:1 1 auto` (grabs ALL space left of the box) and the Segment box
// is pinned to the far right, so — sidepane OPEN — the wheel is shoved LEFT and
// CLIPPED 85px off the canvas edge (svg −85→647 in a 0→922 pane) while the Segment
// box's right edge (922) BUTTS the Segment-Settings sidepane (left 922) with a 0px
// gutter; and — sidepane CLOSED — the box is crammed against the icon rail
// (994→1354, cx 1174 of a 1354 pane) with a dead gap mid-canvas. Neither state is
// balanced. ON (this flag), when a Segment is open at desktop (>=1200, i.e. the
// legacy non-responsive path only), the wheel column stops grabbing the row (it is
// bounded to a pane-relative width so its own ResizeObserver re-fits the wheel with
// NO clip), the wheel+box pair is centred with a gap so the wheel lands ~first
// third and the box ~second third, and the box carries a guaranteed right gutter so
// it can NEVER touch the sidepane/rail. Because the bound is a % of the animated
// canvas pane (64vw sidepane-open / 94vw closed), it RECALCULATES on every sidepane
// toggle. Exact simultaneous third-line centring of BOTH boxes is geometrically
// impossible at these element sizes (in the 922px open pane it would force a ~250px
// wheel), so this is the balanced-distribution interpretation Gordon sanctioned.
// OFF (default) keeps the builder BYTE-IDENTICAL: the wheel column keeps
// `flex:1 1 auto`, the box keeps its far-right position, no gutter/gap is added.
// Pure layout — touches no money/auth/network path. Bug #502 (ISP-9999,
// 2026-08-20): this sat allowlisted OFF BY DECISION with no deploy-workflow
// declaration, so web-stg was dark by omission — declared ON in
// DevOps-Sandbox.yml now (code stays default-OFF; see
// segmentAlign502.deploy.test.js).
export const BUILDER_SEGMENT_THIRDS_ENABLED = truthy(
  process.env.REACT_APP_BUILDER_SEGMENT_THIRDS
);

// LEAD_INBOX_ENABLED — the "Leads" inbox (inspiration I1, FABLE HAIL MARY wave6;
// spec fable-inspiration/LEAD-INBOX-spec.md). OFF (default) keeps the app
// byte-identical to today: the Leads tab is NOT added to the pages/Referrals
// options map, the LeadInbox chunk is never imported (lazy import behind the
// flag — zero new bytes shipped), the flag-gated "View Leads" item is not
// rendered in the Wheel-card three-dot menu, and `/referrals?tab=leads` falls
// back to the default tab via the existing unknown-key logic. ON surfaces the
// creator's OWN captured lead rows (people who unlocked their Wheels with an
// email/answers — CONSENT TIER A only) as a browsable, exportable list. Pairs
// with the backend LEAD_INBOX_ENABLED Django setting: while the BE flag is OFF
// the `/api/v2/wheel/leads/` routes 404, and the flag-ON tab shows its standard
// error state (that is correct, not a bug). Owner-scoped read only; touches no
// money/auth write path (the one shared BE write path — guest permission
// capture — is fire-and-forget behind the BE flag, spec §4.4/§6). Feature name
// "Leads" is a PLACEHOLDER pending Management naming review.
export const LEAD_INBOX_ENABLED = truthy(process.env.REACT_APP_LEAD_INBOX);

// RESPONSIVE_BUILDER_ENABLED — the Wheel Builder tablet-floor responsive reflow
// (wave6 responsive-retrofit U2; spec 02-EDIT-WHEEL §2–§5). OFF (default) keeps
// /builder/:id BYTE-IDENTICAL to today at EVERY width: the desktop 3-column
// react-spring vw layout renders unchanged and NONE of the responsive code path
// runs (every reflow branch is guarded by `RESPONSIVE_BUILDER_ENABLED && mode
// !== 'desktop'`, so at >=1200 it is inert even when ON — the desktop stays
// pixel-locked). ON makes the builder reflow cleanly down to tablet-portrait
// 768: at 1024–1199 the panel becomes a fixed-px in-grid column and the selected
// Segment preview stacks below the wheel; at 768–1023 the tool panel leaves the
// flow and renders through the scrimless side-anchored PanelDrawer (400px) while
// the wheel canvas stays >=240px visible. Kills the min-width:800 pin below 900,
// the vw-animated panes, and the 100vw shells that stacked past the viewport.
// Phone <768 (Quick-Edit) is a SEPARATE unit (U3). Touches no money/auth/network
// path — pure layout. Ships OFF; Gordon flips it after a measured QA pass.
export const RESPONSIVE_BUILDER_ENABLED = truthy(
  process.env.REACT_APP_RESPONSIVE_BUILDER
);

// PROFILE_CARD_LAYOUT_V2 (ISP-9999) — two-column rework of the gallery profile
// card (Gordon 2026-07-20 layout call: "(i) in line with the #s", "LinkVault
// constrained beneath the stats", "Description owns the whole right side",
// "Share button -> 3-dots inside the LinkVault").
//   • OFF (default, unset/empty): BYTE-IDENTICAL to today — Profile/index.jsx
//     renders the existing single-column card exactly as now; ProfileCardV2 is
//     imported but never rendered.
//   • Value "true" / "1" / "split": Gordon's baseline two-column layout —
//     LEFT: avatar+name, stats row with the (i) privacy tooltip inline, the
//     LinkVault chip constrained beneath the stats with its share action moved
//     into an in-chip 3-dots menu (share capability preserved, the standalone
//     Share square is retired in V2 only); RIGHT: the description owns the full
//     card height with the card 3-dots menu kept top-right.
//   • Value "showcase": the split layout PLUS a one-click quick-links rail —
//     each enabled LinkVault link renders as a directly-clickable favicon
//     button (existing onClickIcon open-in-new-tab handler; the in-chip
//     AvatarGroup was decorative-only). "+N" opens the full LinkVault as before.
// REVIEW AFFORDANCE (only when the flag is ON): localStorage
// "iyuga.profileCardLayoutVariant" = "current" | "split" | "showcase" overrides
// the build-time variant per browser so reviewers can A/B live without a
// rebuild. No effect whatsoever when the flag is OFF. Reads happen once per
// Profile mount. Touches no money/auth/network path.
const PROFILE_CARD_LAYOUT_RAW = process.env.REACT_APP_PROFILE_CARD_LAYOUT_V2;

export const PROFILE_CARD_LAYOUT_VARIANTS = ["current", "split", "showcase"];

export const PROFILE_CARD_LAYOUT_V2_ENABLED =
  truthy(PROFILE_CARD_LAYOUT_RAW) ||
  PROFILE_CARD_LAYOUT_RAW === "split" ||
  PROFILE_CARD_LAYOUT_RAW === "showcase";

export const getProfileCardLayoutVariant = () => {
  if (!PROFILE_CARD_LAYOUT_V2_ENABLED) return "current";
  try {
    const override = window.localStorage.getItem(
      "iyuga.profileCardLayoutVariant"
    );
    if (PROFILE_CARD_LAYOUT_VARIANTS.includes(override)) return override;
  } catch (_) {
    // localStorage unavailable (SSR/privacy mode) — fall through to env value.
  }
  return PROFILE_CARD_LAYOUT_RAW === "showcase" ? "showcase" : "split";
};

// HELP_UNIFIED_ENABLED — the single unified "How can we help?" Help page
// (design-handoff/concepts/help-elevated / 01-help-fable). Gordon 2026-07-14:
// "we are going with this style ... remove the obsolete other help tabs and
// merge in whatever is valuable." Supersedes the ISP-4012 4-route split (PR
// #1773, closed). OFF (default) keeps every help surface BYTE-IDENTICAL to
// today: /help, /faq and /support all render the existing <Help/> page
// unchanged, no /contact route exists, and the elevated page + its redirects
// are never mounted. ON renders the elevated single page — gradient hero with
// the line-art Wheel motif, a colored 3x2 topic-card grid, FAQ accordions with
// matching color chips, and the inline contact form — at /help, and collapses
// the old routes into it: /faq -> /help?section=faq and /support + /contact ->
// /help?section=support (client redirects, so existing deep links are
// preserved). The page reuses the SAME data (helpContent HELP_CATEGORIES), the
// SAME FAQ read (useGetFAQ -> GET modules/faq/) and the SAME contact POST
// (useSubmitSupportRequest -> /api/v1/support/contact/) as the legacy page.
// Touches no money/auth path.
export const HELP_UNIFIED_ENABLED = truthy(process.env.REACT_APP_HELP_UNIFIED);

// BUSINESS_BOX_WHEEL_LINK_FIX_ENABLED — repairs the "This Wheel Only" Business
// Box leg end-to-end. Three defects, one flag:
//   (a) LINK. After creating a Business Box from the wheel builder, the applied
//       object written into Redux was the raw Formik draft (businessBoxv2InitialValues
//       has no `id`). The wheel save payload serializes `business_box` verbatim and
//       the backend links with `instance.business_box_id = business_box.get("id")`,
//       so the wheel persisted with business_box_id = NULL — the Apply appeared to
//       succeed and the wheel was never linked. ON applies the CREATE RESPONSE
//       (server truth, carries the id) merged over the draft.
//   (b) VIEW GATE. BusinessBoxBuilder rendered the wheel's box on the wheel view
//       only when that box id appeared in the *viewer's own* Business Box
//       collection (fetched for getLoggedInUser().id) — never true for a visitor,
//       so every visitor saw the default promo. ON drops the viewer-collection
//       condition; the wheel API response is already the authority.
//   (c) OWNER FALLBACK. When a wheel carries no box, ViewWheel fell back to the
//       LOGGED-IN VIEWER's gallery box, painting the viewer's own promo onto
//       someone else's wheel. ON restricts that fallback to the wheel owner; the
//       backend already resolves the owner's base box for everyone else.
// OFF (default) keeps all three paths BYTE-IDENTICAL to today. UI/render only —
// no money, auth, or new network call is introduced by this flag.
export const BUSINESS_BOX_WHEEL_LINK_FIX_ENABLED = truthy(
  process.env.REACT_APP_FIX_BUSINESS_BOX_WHEEL_LINK
);

// CAPTURE_SEGMENT_DROP_ENABLED — put a captured item STRAIGHT into a Segment
// (Kelly, VP Sales, QA-52/QA-53, reported three times).
//
// Today the only route is seven unsignposted steps: builder > + Segment > Build
// pane > drag an EMPTY media block in > its BASIC tab > "Reuse from your Media
// Library" > Captures tab > Insert. The capability works; nothing on screen
// mentions it. Kelly's own words: "A hidden method to bring capture into a Wheel
// does nobody any good."
//
// ON adds two ADDITIVE routes on each card in the builder's "Captured items on
// this wheel" drawer:
//   1. DRAG the capture card onto an open Segment — the block type is inferred
//      from the capture's own type (video -> Video block, photo -> Image, audio
//      -> Audio, note -> Text), so no empty tool is dragged in first.
//   2. An explicit "Add to Segment" control that opens a picker listing EVERY
//      segment in the wheel. Not merely a fallback: drag is unreachable by
//      keyboard and by screen reader, and this is the primary route on touch.
// The new block ALWAYS appends at the very bottom of the segment's existing
// content — it never replaces or prepends.
//
// The existing Media-Library route is untouched and still works. OFF (default)
// renders the drawer byte-identical to today (detach-only, no drag handlers).
export const CAPTURE_SEGMENT_DROP_ENABLED = truthy(
  process.env.REACT_APP_CAPTURE_SEGMENT_DROP
);

// USER_BLOCKING_ENABLED — the Block control in the 1:1 conversation menu
// (#18 Gap #1). Default is now **ON** (unset env => enabled): the backend half
// LANDED (iYugaSupport/Backend PR #2511 — the `messaging.Block` model +
// block/unblock/block-status endpoints, wired into `policy.can_interact` so a
// block severs DM both ways). Set REACT_APP_USER_BLOCKING="false" to force it
// off on a given build.
//
// This is transitively gated by CHAT_ENABLED (the master chat flag, still
// env-driven, default OFF): the Block control only renders inside the 1:1
// conversation menu, which only exists when chat is enabled. So flipping this
// default ON exposes nothing on an environment where chat is dark.
//
// NOTE: the backend endpoints themselves 404 unless CHAT_ENABLED is on in the
// target environment (fail-closed). Live bP/vP verification on web-stg (a
// 2-account block severs sending, BlockedBar shows, unblock restores) is OWED
// before this is called "verified".
//
// Report is deliberately NOT behind this flag: it is real today (the shared
// ReportModal posts to the long-shipped /api/v1/report/), so gating it would
// keep a working safety control switched off for no reason.
export const USER_BLOCKING_ENABLED = truthy(
  process.env.REACT_APP_USER_BLOCKING ?? "true"
);

// PASSKEYS_ENABLED — FEAT-L9 passkeys/WebAuthn. Pairs with the backend
// PASSKEYS_ENABLED Django setting (chronosome_36787/settings/base.py:499),
// which 404s all four/six login/passkey/* endpoints while off, live-confirmed
// still False on web-stg 2026-08-31 (registration_options/registration_verify/
// authentication_options/authentication_verify/credentials all returned 404
// with a valid staging token). OFF (default) keeps every surface
// byte-identical to today: Settings > Security renders no "Passkeys" section
// and the Login page renders no "Sign in with a passkey" option, so
// @simplewebauthn/browser's startRegistration/startAuthentication are never
// called and no /api/v1/login/passkey/* request is ever issued. ON adds (1) a
// Passkeys section in Settings > Security to add/list/remove a passkey via
// the registration_options -> startRegistration -> registration_verify round
// trip, and (2) a "Sign in with a passkey" link on the Login page that runs
// authentication_options -> startAuthentication -> authentication_verify and
// feeds the returned {token, "2FA", user} into the SAME onLoginSuccess path
// password login already uses (identical response shape, per
// PasskeyAuthenticationVerifyViewSet's own doc comment). Touches no
// money/auth-model path — passkeys are an additional credential on the
// existing Token/2FA session, not a new auth system.
export const PASSKEYS_ENABLED = truthy(process.env.REACT_APP_PASSKEYS_ENABLED);
